/**
 * DocumentsControlScreen.js
 * JOBENG – Full-featured document management
 * Features: CRUD, upload, versioning, approval workflow, reject with reason,
 * bulk actions, grid/list toggle, advanced search, multi-filter, sort,
 * analytics dashboard, comments, reminders, favourites, pin/lock,
 * share, duplicate, archive/restore, delete, add version, expiry alerts,
 * category & access-level chips, FlatList as root (no ScrollView nesting),
 * React.memo cards, modal gating, API integration with AsyncStorage cache.
 */

import React, {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, Modal, TextInput,
  Alert, Dimensions, FlatList, RefreshControl, ActivityIndicator,
  BackHandler, Share, Animated, Platform, Image,
} from 'react-native';
import { SafeAreaView }            from 'react-native-safe-area-context';
import { MaterialCommunityIcons }  from '@expo/vector-icons';
import { Ionicons }                from '@expo/vector-icons';
import { LinearGradient }          from 'expo-linear-gradient';
import AsyncStorage                from '@react-native-async-storage/async-storage';
import axios                       from 'axios';
import * as DocumentPicker         from 'expo-document-picker';
import * as Print                  from 'expo-print';
import * as Sharing                from 'expo-sharing';
import * as Linking                from 'expo-linking';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';

import AuditTrailModal       from './AuditTrailModal';
import DocumentPreviewModal  from './DocumentPreviewModal';

const { width, height } = Dimensions.get('window');

// ─── iPhone 16 Pro Max display constants ─────────────────────────────────────
const IS_PROMAX  = width >= 430;
const W          = width;
const HPAD       = IS_PROMAX ? 18 : 14;
const CARD_R     = IS_PROMAX ? 16 : 12;
const MODAL_R    = IS_PROMAX ? 24 : 20;
const TOUCH_H    = IS_PROMAX ? 48 : 44;
const ICON_BTN   = IS_PROMAX ? 40 : 34;
const AVATAR_SM  = IS_PROMAX ? 48 : 42;
const AVATAR_LG  = IS_PROMAX ? 84 : 72;
const FAB_SIZE   = IS_PROMAX ? 62 : 56;
const FAB_BOTTOM = IS_PROMAX ? 38 : 28;
const FS_XS      = IS_PROMAX ? 11 : 10;
const FS_SM      = IS_PROMAX ? 13 : 11;
const FS_BASE    = IS_PROMAX ? 15 : 13;
const FS_MD      = IS_PROMAX ? 16 : 14;
const FS_LG      = IS_PROMAX ? 18 : 16;
const FS_XL      = IS_PROMAX ? 20 : 17;

// ─────────────────────────────────────────────────────────────────────────────
// BRAND PALETTE
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  primary:   '#0e4e96',
  accent:    '#19abb5',
  gold:      '#fdb913',
  success:   '#10b981',
  warning:   '#f59e0b',
  danger:    '#dc2626',
  purple:    '#8b5cf6',
  bg:        '#f1f5f9',
  card:      '#ffffff',
  border:    '#e2e8f0',
  dark:      '#0f172a',
  mid:       '#475569',
  light:     '#94a3b8',
};

// ─────────────────────────────────────────────────────────────────────────────
// API
// ─────────────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const api = axios.create({
  baseURL: API_BASE,
  headers: { 'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef', 'Content-Type': 'application/json' },
  timeout: 12000,
});

// Attach auth token + company_id on every request
api.interceptors.request.use(async (config) => {
  try {
    const token = await AsyncStorage.getItem('auth_token') || await AsyncStorage.getItem('token');
    if (token) config.headers['Authorization'] = `Bearer ${token}`;
    const bizId = await AsyncStorage.getItem('company_id');
    if (bizId) {
      config.params = config.params || {};
      config.params.company_id = parseInt(bizId);
    }
  } catch {}
  return config;
});
const EP = {
  // ── Document CRUD (old server routes — still work for GET list + single) ──
  docs:           '/api/documents',
  doc:      (id)=> `/api/docs/${id}`,
  // ── Document Operations → new /api/docs router ─────────────────────────
  approve:  (id)=> `/api/docs/${id}/approve`,
  reject:   (id)=> `/api/docs/${id}/reject`,
  archive:  (id)=> `/api/docs/${id}/archive`,
  restore:  (id)=> `/api/docs/${id}/restore`,
  pin:      (id)=> `/api/docs/${id}/pin`,
  favourite:(id)=> `/api/docs/${id}/favourite`,
  lock:     (id)=> `/api/docs/${id}/lock`,
  duplicate:(id)=> `/api/docs/${id}/duplicate`,
  download: (id)=> `/api/docs/${id}/download`,
  share:    (id)=> `/api/docs/${id}/share`,
  // ── Versions ──────────────────────────────────────────────────────────
  versions: (id)=> `/api/docs/${id}/versions`,
  version:  (id,v)=> `/api/docs/${id}/versions/${v}`,
  // ── Comments ──────────────────────────────────────────────────────────
  comments:         (id)     => `/api/docs/${id}/comments`,
  resolveComment:   (id, cid)=> `/api/docs/${id}/comments/${cid}/resolve`,
  deleteComment:    (id, cid)=> `/api/docs/${id}/comments/${cid}`,
  // ── Reminders ─────────────────────────────────────────────────────────
  reminders:  (id)      => `/api/docs/${id}/reminders`,
  reminder:   (id, rid) => `/api/docs/${id}/reminders/${rid}`,
  // ── Share Trail ───────────────────────────────────────────────────────
  shares:     (id)=> `/api/docs/${id}/shares`,
  // ── Tags (old routes) ─────────────────────────────────────────────────
  tagsList:              '/api/documents/tags',
  tagDocs:    (tag)   => `/api/documents/tags/${tag}/documents`,
  docTags:    (id)    => `/api/documents/${id}/tags`,
  docTag:     (id,tag)=> `/api/documents/${id}/tags/${tag}`,
  // ── Categories & Types (old routes) ───────────────────────────────────
  categories:    '/api/documents/categories',
  types:         '/api/documents/types',
  accessLevels:  '/api/documents/access-levels',
  // ── Analytics → new /api/docs router ──────────────────────────────────
  analytics:         '/api/docs/dashboard',
  analyticsByCat:    '/api/docs/summary',
  analyticsByType:   '/api/docs/summary',
  analyticsByAccess: '/api/docs/summary',
  mostViewed:        '/api/docs/dashboard',
  mostDownloaded:    '/api/docs/dashboard',
  expiringDocs:      '/api/docs/expiring',
};

// ─────────────────────────────────────────────────────────────────────────────
// REFERENCE DATA
// ─────────────────────────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'general',     label: 'General',     icon: 'file-document',          color: '#607d8b' },
  { id: 'hr',          label: 'HR',          icon: 'account-group',          color: '#10b981' },
  { id: 'compliance',  label: 'Compliance',  icon: 'shield-check',           color: '#2196f3' },
  { id: 'finance',     label: 'Finance',     icon: 'currency-gbp',         color: '#f59e0b' },
  { id: 'safety',      label: 'Safety',      icon: 'shield-alert',           color: '#dc2626' },
  { id: 'projects',    label: 'Projects',    icon: 'folder-multiple',        color: '#8b5cf6' },
  { id: 'legal',       label: 'Legal',       icon: 'scale-balance',          color: '#0e4e96' },
  { id: 'engineering', label: 'Engineering', icon: 'cog',                    color: '#19abb5' },
  { id: 'contracts',   label: 'Contracts',   icon: 'file-sign',              color: '#fdb913' },
  { id: 'technical',   label: 'Technical',   icon: 'wrench',                 color: '#475569' },
  { id: 'quality',     label: 'Quality',     icon: 'certificate',            color: '#e91e63' },
  { id: 'procurement', label: 'Procurement', icon: 'cart-outline',           color: '#ff5722' },
];

const FILE_TYPES = [
  { id: 'pdf',  label: 'PDF',   icon: 'file-pdf-box',         color: '#dc2626' },
  { id: 'docx', label: 'Word',  icon: 'file-word-box',        color: '#2196f3' },
  { id: 'xlsx', label: 'Excel', icon: 'file-excel-box',       color: '#10b981' },
  { id: 'pptx', label: 'PPT',   icon: 'file-powerpoint-box',  color: '#f59e0b' },
  { id: 'dwg',  label: 'DWG',   icon: 'vector-square',        color: '#8b5cf6' },
  { id: 'dxf',  label: 'DXF',   icon: 'vector-line',          color: '#19abb5' },
  { id: 'img',  label: 'Image', icon: 'file-image',           color: '#607d8b' },
  { id: 'zip',  label: 'ZIP',   icon: 'folder-zip',           color: '#0e4e96' },
  { id: 'txt',  label: 'Text',  icon: 'file-document-outline',color: '#94a3b8' },
  { id: 'mp4',  label: 'Video', icon: 'file-video',           color: '#e91e63' },
];

const ACCESS_LEVELS = [
  { id: 'public',       label: 'Public',        icon: 'earth',           color: '#10b981' },
  { id: 'internal',     label: 'Internal',      icon: 'office-building', color: '#0e4e96' },
  { id: 'restricted',   label: 'Restricted',    icon: 'lock',            color: '#f59e0b' },
  { id: 'confidential', label: 'Confidential',  icon: 'lock-alert',      color: '#dc2626' },
  { id: 'top_secret',   label: 'Top Secret',    icon: 'eye-off',         color: '#4a0404' },
];

const STATUSES = [
  { id: 'draft',        label: 'Draft',        color: '#94a3b8' },
  { id: 'active',       label: 'Active',       color: '#10b981' },
  { id: 'under_review', label: 'Under Review', color: '#f59e0b' },
  { id: 'approved',     label: 'Approved',     color: '#10b981' },
  { id: 'rejected',     label: 'Rejected',     color: '#dc2626' },
  { id: 'archived',     label: 'Archived',     color: '#94a3b8' },
  { id: 'expired',      label: 'Expired',      color: '#dc2626' },
  { id: 'superseded',   label: 'Superseded',   color: '#475569' },
];

const SORT_OPTIONS = [
  { id: 'newest',    label: 'Newest Modified' },
  { id: 'oldest',    label: 'Oldest Modified' },
  { id: 'created',   label: 'Date Created' },
  { id: 'az',        label: 'Name A → Z' },
  { id: 'za',        label: 'Name Z → A' },
  { id: 'size_desc', label: 'Largest First' },
  { id: 'size_asc',  label: 'Smallest First' },
  { id: 'views',     label: 'Most Viewed' },
  { id: 'downloads', label: 'Most Downloaded' },
  { id: 'expiry',    label: 'Expiring Soonest' },
];

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const getCat    = (id) => CATEGORIES.find(c => c.id === id)    || CATEGORIES[0];
const getType   = (id) => FILE_TYPES.find(t => t.id === id)    || FILE_TYPES[0];
const getAccess = (id) => ACCESS_LEVELS.find(a => a.id === id) || ACCESS_LEVELS[0];
const getStatus = (id) => STATUSES.find(s => s.id === id)      || STATUSES[1];

const parseBytes = (str) => {
  if (!str) return 0;
  const n = parseFloat(str);
  if (/GB/i.test(str)) return n * 1e9;
  if (/MB/i.test(str)) return n * 1e6;
  if (/KB/i.test(str)) return n * 1e3;
  return n;
};

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - Date.now()) / 86400000);
};

const fmtDate = (str) => {
  if (!str) return '—';
  try { return new Date(str).toLocaleDateString(); } catch { return str; }
};

const nowISO  = () => new Date().toISOString();
const todayStr= () => new Date().toISOString().split('T')[0];

const makeId  = (prefix = 'doc') => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2,7)}`;

const defaultUploadForm = () => ({
  name: '', category: 'general', type: 'pdf', description: '',
  tags: '', accessLevel: 'internal', expiryDate: '', reviewDate: '',
  author: 'James Walker', department: '', reference: '',
  pickedFile: null,   // { name, uri, size, mimeType }
});

// ─────────────────────────────────────────────────────────────────────────────
// SAMPLE DATA  (12 rich documents across all categories)
// ─────────────────────────────────────────────────────────────────────────────
const SAMPLE = [
  {
    id:'doc_001', name:'Employee Handbook 2024', category:'hr', type:'pdf',
    size:'2.5 MB', sizeBytes:2621440, status:'active', accessLevel:'internal',
    version:'3.2', createdDate:'2024-01-15', modifiedDate:'2024-01-20',
    expiryDate:'2026-12-31', reviewDate:'2024-06-30',
    author:'HR Department', department:'Human Resources', reference:'HR-2024-001',
    description:'Comprehensive employee handbook covering policies, procedures, and benefits.',
    tags:['hr','policies','procedures','benefits'],
    downloadCount:156, viewCount:423, approvalStatus:'approved',
    approvedBy:'John Smith', approvalDate:'2024-01-18',
    pinned:false, favourite:true, locked:false,
    checksum:'a1b2c3d4e5f6', location:'/documents/hr/handbook_2024.pdf',
    shareTrail:[{ sharedWith:'james.walker@jobeng.ai', date:'2024-01-21', by:'HR Department' }],
    comments:[{ id:'c1', author:'James Walker', avatar:'J', text:'Updated for 2024 changes.', date:'2024-01-20', resolved:false }],
    reminders:[{ id:'r1', date:'2024-06-01', note:'Schedule mid-year review', triggered:false }],
    versions:[
      { version:'3.2', date:'2024-01-20', author:'HR Department', changes:'Updated benefits section', sizeBytes:2621440 },
      { version:'3.1', date:'2024-01-15', author:'HR Department', changes:'Added remote work policy', sizeBytes:2500000 },
      { version:'3.0', date:'2023-12-01', author:'HR Department', changes:'Annual review update', sizeBytes:2400000 },
    ],
  },
  {
    id:'doc_002', name:'ISO 9001:2015 Certificate', category:'compliance', type:'pdf',
    size:'1.2 MB', sizeBytes:1258291, status:'active', accessLevel:'public',
    version:'1.0', createdDate:'2023-11-10', modifiedDate:'2023-11-10',
    expiryDate:'2026-11-10', reviewDate:'2025-11-10',
    author:'Quality Assurance', department:'Quality', reference:'QA-CERT-001',
    description:'ISO 9001:2015 Quality Management System Certificate.',
    tags:['iso','quality','certificate','compliance'],
    downloadCount:89, viewCount:234, approvalStatus:'approved',
    approvedBy:'Quality Manager', approvalDate:'2023-11-10',
    pinned:true, favourite:false, locked:false,
    checksum:'f6e5d4c3b2a1', location:'/documents/compliance/iso9001_cert.pdf',
    shareTrail:[], comments:[], reminders:[],
    versions:[{ version:'1.0', date:'2023-11-10', author:'Quality Assurance', changes:'Initial certificate', sizeBytes:1258291 }],
  },
  {
    id:'doc_003', name:'Financial Report Q4 2023', category:'finance', type:'xlsx',
    size:'5.8 MB', sizeBytes:6082355, status:'archived', accessLevel:'confidential',
    version:'2.1', createdDate:'2024-01-05', modifiedDate:'2024-01-12',
    expiryDate:'2027-01-05', reviewDate:'2025-01-05',
    author:'Finance Team', department:'Finance', reference:'FIN-Q4-2023',
    description:'Quarterly financial report including P&L, balance sheet, and cash flow statements.',
    tags:['finance','quarterly','report','q4','2023'],
    downloadCount:45, viewCount:78, approvalStatus:'approved',
    approvedBy:'CFO', approvalDate:'2024-01-10',
    pinned:false, favourite:true, locked:true,
    checksum:'b2c3d4e5f6a1', location:'/documents/finance/q4_2023_report.xlsx',
    shareTrail:[], comments:[], reminders:[],
    versions:[
      { version:'2.1', date:'2024-01-12', author:'Finance Team', changes:'Corrected cash flow figures', sizeBytes:6082355 },
      { version:'2.0', date:'2024-01-08', author:'Finance Team', changes:'Added detailed analysis', sizeBytes:5900000 },
      { version:'1.0', date:'2024-01-05', author:'Finance Team', changes:'Initial draft', sizeBytes:5700000 },
    ],
  },
  {
    id:'doc_004', name:'Site Safety Procedures Manual', category:'safety', type:'pdf',
    size:'3.2 MB', sizeBytes:3355443, status:'under_review', accessLevel:'internal',
    version:'4.0', createdDate:'2024-01-22', modifiedDate:'2024-01-25',
    expiryDate:'2025-01-22', reviewDate:'2024-07-22',
    author:'Safety Officer', department:'HSQE', reference:'HSE-MAN-004',
    description:'Comprehensive safety procedures and emergency protocols for all site operations.',
    tags:['safety','procedures','emergency','protocols','site'],
    downloadCount:67, viewCount:145, approvalStatus:'pending',
    approvedBy:null, approvalDate:null,
    pinned:false, favourite:false, locked:false,
    checksum:'c3d4e5f6a1b2', location:'/documents/safety/procedures_manual_v4.pdf',
    shareTrail:[],
    comments:[{ id:'c2', author:'Site Manager', avatar:'S', text:'Please review section 4.2 on fall protection before approving.', date:'2024-01-24', resolved:false }],
    reminders:[{ id:'r2', date:'2024-04-01', note:'Chase approval from HSE Director', triggered:false }],
    versions:[
      { version:'4.0', date:'2024-01-25', author:'Safety Officer', changes:'Updated fire evacuation procedures', sizeBytes:3355443 },
      { version:'3.5', date:'2024-01-22', author:'Safety Officer', changes:'Added COSHH assessments', sizeBytes:3200000 },
    ],
  },
  {
    id:'doc_005', name:'Project Charter – Alpha Initiative', category:'projects', type:'docx',
    size:'1.8 MB', sizeBytes:1887437, status:'active', accessLevel:'restricted',
    version:'1.3', createdDate:'2024-01-18', modifiedDate:'2024-01-24',
    expiryDate:'2024-12-31', reviewDate:'2024-06-18',
    author:'Project Manager', department:'Projects', reference:'PM-ALPHA-001',
    description:'Project charter for Alpha Initiative including scope, objectives, budget, and timeline.',
    tags:['project','charter','alpha','initiative','scope'],
    downloadCount:23, viewCount:56, approvalStatus:'approved',
    approvedBy:'Director', approvalDate:'2024-01-20',
    pinned:true, favourite:true, locked:false,
    checksum:'d4e5f6a1b2c3', location:'/documents/projects/alpha_charter_v1.3.docx',
    shareTrail:[{ sharedWith:'board@jobeng.ai', date:'2024-01-21', by:'Project Manager' }],
    comments:[], reminders:[{ id:'r3', date:'2024-06-01', note:'Mid-year scope review', triggered:false }],
    versions:[
      { version:'1.3', date:'2024-01-24', author:'Project Manager', changes:'Updated timeline and milestones', sizeBytes:1887437 },
      { version:'1.2', date:'2024-01-20', author:'Project Manager', changes:'Added risk assessment matrix', sizeBytes:1800000 },
      { version:'1.1', date:'2024-01-19', author:'Project Manager', changes:'Refined scope definition', sizeBytes:1750000 },
      { version:'1.0', date:'2024-01-18', author:'Project Manager', changes:'Initial draft', sizeBytes:1700000 },
    ],
  },
  {
    id:'doc_006', name:'Site A Engineering Drawings Rev B', category:'engineering', type:'dwg',
    size:'8.4 MB', sizeBytes:8809038, status:'active', accessLevel:'restricted',
    version:'2.0', createdDate:'2024-01-10', modifiedDate:'2024-01-22',
    expiryDate:null, reviewDate:'2024-07-10',
    author:'Lead Engineer', department:'Engineering', reference:'ENG-SITE-A-002',
    description:'Full set of engineering drawings for Site A: structural, mechanical, and electrical.',
    tags:['engineering','drawing','site-a','structural','mechanical'],
    downloadCount:12, viewCount:38, approvalStatus:'approved',
    approvedBy:'Chief Engineer', approvalDate:'2024-01-22',
    pinned:false, favourite:false, locked:false,
    checksum:'e5f6a1b2c3d4', location:'/documents/engineering/site_a_rev_b.dwg',
    shareTrail:[], comments:[], reminders:[],
    versions:[
      { version:'2.0', date:'2024-01-22', author:'Lead Engineer', changes:'Updated electrical layout Rev B', sizeBytes:8809038 },
      { version:'1.0', date:'2024-01-10', author:'Lead Engineer', changes:'Initial drawing set', sizeBytes:8500000 },
    ],
  },
  {
    id:'doc_007', name:'Framework Contract – BuildCo Ltd', category:'contracts', type:'pdf',
    size:'4.1 MB', sizeBytes:4299161, status:'active', accessLevel:'confidential',
    version:'1.0', createdDate:'2024-01-08', modifiedDate:'2024-01-08',
    expiryDate:'2026-01-08', reviewDate:'2025-01-08',
    author:'Legal Team', department:'Legal', reference:'LGL-FW-2024-001',
    description:'Framework contract with BuildCo Ltd covering rates, terms, and conditions for 2024–2026.',
    tags:['contract','buildco','framework','legal','rates'],
    downloadCount:8, viewCount:21, approvalStatus:'approved',
    approvedBy:'Managing Director', approvalDate:'2024-01-08',
    pinned:false, favourite:false, locked:true,
    checksum:'f1a2b3c4d5e6', location:'/documents/contracts/buildco_framework.pdf',
    shareTrail:[], comments:[],
    reminders:[{ id:'r4', date:'2025-10-01', note:'Renegotiation notice period begins', triggered:false }],
    versions:[{ version:'1.0', date:'2024-01-08', author:'Legal Team', changes:'Signed and executed', sizeBytes:4299161 }],
  },
  {
    id:'doc_008', name:'UK GDPR Compliance Policy v2.3', category:'legal', type:'pdf',
    size:'0.9 MB', sizeBytes:943718, status:'active', accessLevel:'internal',
    version:'2.3', createdDate:'2023-05-18', modifiedDate:'2024-01-03',
    expiryDate:null, reviewDate:'2024-05-18',
    author:'Data Protection Officer', department:'Legal', reference:'LGL-GDPR-002',
    description:'Company GDPR compliance policy, lawful bases for processing, data handling procedures, and subject rights.',
    tags:['gdpr','legal','data','compliance','privacy','dpo'],
    downloadCount:104, viewCount:289, approvalStatus:'approved',
    approvedBy:'DPO', approvalDate:'2024-01-03',
    pinned:false, favourite:true, locked:false,
    checksum:'a2b3c4d5e6f1', location:'/documents/legal/gdpr_policy_v2.3.pdf',
    shareTrail:[], comments:[], reminders:[],
    versions:[
      { version:'2.3', date:'2024-01-03', author:'DPO', changes:'Updated for UK GDPR post-Brexit changes', sizeBytes:943718 },
      { version:'2.2', date:'2023-05-18', author:'DPO', changes:'Annual review', sizeBytes:930000 },
    ],
  },
  {
    id:'doc_009', name:'Tender Submission – CS-2024-087', category:'procurement', type:'pdf',
    size:'12.3 MB', sizeBytes:12897484, status:'active', accessLevel:'confidential',
    version:'1.0', createdDate:'2024-01-29', modifiedDate:'2024-01-30',
    expiryDate:'2024-03-31', reviewDate:null,
    author:'Bid Manager', department:'Commercial', reference:'COM-TEND-087',
    description:'Complete tender submission package for contract CS-2024-087 including technical and commercial envelopes.',
    tags:['tender','bid','cs-2024-087','commercial','submission'],
    downloadCount:3, viewCount:14, approvalStatus:'approved',
    approvedBy:'Commercial Director', approvalDate:'2024-01-30',
    pinned:true, favourite:false, locked:false,
    checksum:'b3c4d5e6f1a2', location:'/documents/procurement/tender_cs2024087.pdf',
    shareTrail:[], comments:[],
    reminders:[{ id:'r5', date:'2024-03-15', note:'Chase client confirmation of receipt', triggered:false }],
    versions:[{ version:'1.0', date:'2024-01-30', author:'Bid Manager', changes:'Final submitted version', sizeBytes:12897484 }],
  },
  {
    id:'doc_010', name:'Quality Management Procedure QMP-07', category:'quality', type:'docx',
    size:'2.1 MB', sizeBytes:2202009, status:'active', accessLevel:'internal',
    version:'5.1', createdDate:'2022-03-10', modifiedDate:'2024-01-12',
    expiryDate:null, reviewDate:'2024-03-10',
    author:'Quality Manager', department:'Quality', reference:'QMP-07',
    description:'Document control procedure covering creation, review, approval, distribution, and obsolescence of controlled documents.',
    tags:['quality','procedure','document-control','iso','qms'],
    downloadCount:211, viewCount:504, approvalStatus:'approved',
    approvedBy:'Director of Quality', approvalDate:'2024-01-12',
    pinned:false, favourite:false, locked:false,
    checksum:'c4d5e6f1a2b3', location:'/documents/quality/qmp_07_v5.1.docx',
    shareTrail:[], comments:[], reminders:[],
    versions:[
      { version:'5.1', date:'2024-01-12', author:'Quality Manager', changes:'Aligned with ISO 9001:2015 clause 7.5', sizeBytes:2202009 },
      { version:'5.0', date:'2022-03-10', author:'Quality Manager', changes:'Major revision – simplified workflow', sizeBytes:2100000 },
    ],
  },
  {
    id:'doc_011', name:'Technical Method Statement – Piling', category:'technical', type:'pdf',
    size:'6.7 MB', sizeBytes:7025459, status:'under_review', accessLevel:'restricted',
    version:'1.1', createdDate:'2024-01-26', modifiedDate:'2024-01-28',
    expiryDate:'2024-07-26', reviewDate:'2024-04-26',
    author:'Senior Engineer', department:'Engineering', reference:'TMS-PIL-001',
    description:'Method statement for piling operations at Site B including plant, personnel, RAMS, and environmental controls.',
    tags:['method-statement','piling','technical','rams','site-b'],
    downloadCount:5, viewCount:22, approvalStatus:'pending',
    approvedBy:null, approvalDate:null,
    pinned:false, favourite:false, locked:false,
    checksum:'d5e6f1a2b3c4', location:'/documents/technical/tms_piling_v1.1.pdf',
    shareTrail:[], comments:[], reminders:[],
    versions:[
      { version:'1.1', date:'2024-01-28', author:'Senior Engineer', changes:'Addressed client comments', sizeBytes:7025459 },
      { version:'1.0', date:'2024-01-26', author:'Senior Engineer', changes:'Initial issue', sizeBytes:6900000 },
    ],
  },
  {
    id:'doc_012', name:'Board Meeting Minutes – Jan 2024', category:'general', type:'docx',
    size:'0.4 MB', sizeBytes:419430, status:'draft', accessLevel:'confidential',
    version:'0.1', createdDate:'2024-01-31', modifiedDate:'2024-01-31',
    expiryDate:null, reviewDate:null,
    author:'Company Secretary', department:'Executive', reference:'BRD-MIN-2024-01',
    description:'Draft minutes of the January 2024 Board of Directors meeting.',
    tags:['board','minutes','meeting','confidential','draft'],
    downloadCount:0, viewCount:4, approvalStatus:'pending',
    approvedBy:null, approvalDate:null,
    pinned:false, favourite:false, locked:true,
    checksum:'e6f1a2b3c4d5', location:'/documents/general/board_minutes_jan24.docx',
    shareTrail:[], comments:[], reminders:[],
    versions:[{ version:'0.1', date:'2024-01-31', author:'Company Secretary', changes:'First draft', sizeBytes:419430 }],
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// DocumentCard  (React.memo – stable reference, never re-renders unnecessarily)
// ─────────────────────────────────────────────────────────────────────────────
const DocumentCard = React.memo(({ item, viewMode, bulkMode, bulkSelected, onPress, onLongPress }) => {
  const cat    = getCat(item.category);
  const type   = getType(item.type);
  const status = getStatus(item.status);
  const access = getAccess(item.accessLevel);
  const isSel  = bulkSelected.includes(item.id);
  const days   = daysUntil(item.expiryDate);
  const expiring = days !== null && days > 0  && days <= 60;
  const expired  = days !== null && days <= 0;

  /* ── GRID CARD ── */
  if (viewMode === 'grid') {
    return (
      <TouchableOpacity
        style={[s.gridCard, isSel && s.cardSel]}
        onPress={() => onPress(item)}
        onLongPress={() => onLongPress(item)}
        activeOpacity={0.8}
      >
        {bulkMode && (
          <View style={[s.bulkDot, isSel && s.bulkDotOn]}>
            {isSel && <Ionicons name="checkmark" size={11} color="#fff" />}
          </View>
        )}
        {item.pinned   && <View style={s.gridPin}><MaterialCommunityIcons name="pin" size={12} color={C.gold} /></View>}
        {item.locked   && <View style={[s.gridPin, { right: 26 }]}><Ionicons name="lock-closed" size={10} color={C.mid} /></View>}
        {item.favourite&& <View style={[s.gridPin, { right: item.locked ? 46 : 26 }]}><Ionicons name="heart" size={10} color={C.danger} /></View>}
        <View style={[s.gridIconWrap, { backgroundColor:`${cat.color}18` }]}>
          <MaterialCommunityIcons name={type.icon} size={32} color={type.color} />
        </View>
        <Text style={s.gridName} numberOfLines={2}>{item.name}</Text>
        <Text style={s.gridMeta}>{(item.type||'').toUpperCase()} · v{item.version}</Text>
        <View style={[s.gridStatusDot, { backgroundColor: status.color }]} />
        {item.approvalStatus === 'pending' && (
          <View style={s.gridPendBadge}><Text style={s.gridPendText}>Pending</Text></View>
        )}
        {(expiring || expired) && (
          <View style={[s.gridExpBadge, { backgroundColor: expired ? C.danger : C.warning }]}>
            <Text style={s.gridExpText}>{expired ? 'Expired' : `${days}d`}</Text>
          </View>
        )}
      </TouchableOpacity>
    );
  }

  /* ── LIST CARD ── */
  return (
    <TouchableOpacity
      style={[s.card, isSel && s.cardSel,
        expiring && s.cardExpiring,
        expired  && s.cardExpired,
        item.pinned && s.cardPinned,
      ]}
      onPress={() => onPress(item)}
      onLongPress={() => onLongPress(item)}
      activeOpacity={0.8}
    >
      {bulkMode && (
        <View style={[s.bulkDot, isSel && s.bulkDotOn]}>
          {isSel && <Ionicons name="checkmark" size={11} color="#fff" />}
        </View>
      )}

      {/* Row 1: icon + info + badges */}
      <View style={s.cardRow}>
        <View style={[s.cardIcon, { backgroundColor:`${cat.color}18` }]}>
          <MaterialCommunityIcons name={cat.icon} size={22} color={cat.color} />
        </View>
        <View style={{ flex:1, marginLeft:12 }}>
          <View style={s.cardNameRow}>
            <Text style={s.cardName} numberOfLines={1}>{item.name}</Text>
            <View style={s.cardBadges}>
              {item.pinned    && <MaterialCommunityIcons name="pin"          size={13} color={C.gold}   />}
              {item.favourite && <Ionicons name="heart"                       size={13} color={C.danger}  />}
              {item.locked    && <Ionicons name="lock-closed"                 size={11} color={C.mid}     />}
            </View>
          </View>
          <View style={s.cardMeta}>
            <MaterialCommunityIcons name={type.icon} size={12} color={type.color} />
            <Text style={s.cardMetaTxt}>{(item.type||'').toUpperCase()}</Text>
            <Text style={s.dot}>·</Text>
            <Text style={s.cardMetaTxt}>{item.size}</Text>
            <Text style={s.dot}>·</Text>
            <Text style={s.cardMetaTxt}>v{item.version}</Text>
            {item.reference ? <><Text style={s.dot}>·</Text><Text style={s.cardMetaTxt}>{item.reference}</Text></> : null}
          </View>
        </View>
        <View style={{ gap:4, alignItems:'flex-end' }}>
          <View style={[s.statusPill, { backgroundColor:`${status.color}18` }]}>
            <Text style={[s.statusPillTxt, { color:status.color }]}>{status.label}</Text>
          </View>
          <View style={[s.accessPill, { backgroundColor:`${access.color}12` }]}>
            <MaterialCommunityIcons name={access.icon} size={9} color={access.color} />
            <Text style={[s.accessPillTxt, { color:access.color }]}>{access.label}</Text>
          </View>
        </View>
      </View>

      {/* Description */}
      {!!item.description && <Text style={s.cardDesc} numberOfLines={2}>{item.description}</Text>}

      {/* Tags */}
      {item.tags?.length > 0 && (
        <View style={s.tagsRow}>
          {item.tags.slice(0, 5).map((t,i) => <View key={i} style={s.tagChip}><Text style={s.tagChipTxt}>#{t}</Text></View>)}
          {item.tags.length > 5 && <View style={s.tagChip}><Text style={s.tagChipTxt}>+{item.tags.length-5}</Text></View>}
        </View>
      )}

      {/* Footer */}
      <View style={s.cardFooter}>
        <View style={s.cardStats}>
          <View style={s.cardStat}><MaterialCommunityIcons name="eye-outline"      size={12} color={C.light} /><Text style={s.cardStatTxt}>{item.viewCount}</Text></View>
          <View style={s.cardStat}><MaterialCommunityIcons name="download-outline" size={12} color={C.light} /><Text style={s.cardStatTxt}>{item.downloadCount}</Text></View>
          {item.comments?.length > 0 && <View style={s.cardStat}><MaterialCommunityIcons name="comment-outline" size={12} color={C.light} /><Text style={s.cardStatTxt}>{item.comments.length}</Text></View>}
          {item.versions?.length > 1  && <View style={s.cardStat}><MaterialCommunityIcons name="history"         size={12} color={C.light} /><Text style={s.cardStatTxt}>{item.versions.length}</Text></View>}
        </View>
        <View style={{ alignItems:'flex-end', gap:2 }}>
          <Text style={s.cardDate}>Modified {fmtDate(item.modifiedDate)}</Text>
          {item.author ? <Text style={[s.cardDate, { color:C.mid }]}>{item.author}</Text> : null}
        </View>
      </View>

      {/* Alerts */}
      {item.approvalStatus === 'pending' && (
        <View style={s.alertBar}>
          <MaterialCommunityIcons name="clock-alert-outline" size={13} color={C.warning} />
          <Text style={[s.alertBarTxt, { color:C.warning }]}>Awaiting Approval</Text>
        </View>
      )}
      {expiring && (
        <View style={[s.alertBar, { backgroundColor:'#fff7ed' }]}>
          <MaterialCommunityIcons name="calendar-alert" size={13} color={C.warning} />
          <Text style={[s.alertBarTxt, { color:C.warning }]}>Expires in {days} day{days===1?'':'s'} · {fmtDate(item.expiryDate)}</Text>
        </View>
      )}
      {expired && (
        <View style={[s.alertBar, { backgroundColor:'#fef2f2' }]}>
          <MaterialCommunityIcons name="calendar-remove" size={13} color={C.danger} />
          <Text style={[s.alertBarTxt, { color:C.danger }]}>EXPIRED · {fmtDate(item.expiryDate)}</Text>
        </View>
      )}
      {item.status === 'draft' && (
        <View style={[s.alertBar, { backgroundColor:'#f8fafc' }]}>
          <MaterialCommunityIcons name="pencil-outline" size={13} color={C.mid} />
          <Text style={[s.alertBarTxt, { color:C.mid }]}>Draft – not yet submitted for approval</Text>
        </View>
      )}
    </TouchableOpacity>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────────────────────
export default function DocumentsControlScreen({ navigation }) {
  /* ── state ── */
  const [companyId,     setCompanyId]    = useState(null);
  const [companyLogo,   setCompanyLogo]  = useState(null);
  const [companyName,   setCompanyName]  = useState('');
  const [documents,   setDocuments]  = useState([]);
  const [isLoading,   setIsLoading]  = useState(true);
  const [refreshing,  setRefreshing] = useState(false);

  const { ask: askAI, agents: aiAgents, loading: aiLoading, history: aiHistory } = useAI('DocumentsControlScreen');
  const [showAIChat, setShowAIChat] = useState(false);


  const [activeTab,   setActiveTab]  = useState('all');
  const [searchQuery, setSearch]     = useState('');
  const [sortBy,      setSort]       = useState('newest');
  const [viewMode,    setView]       = useState('list');
  const [filters,     setFilters]    = useState({ category:'all', type:'all', status:'all', accessLevel:'all', approvalStatus:'all' });
  const [showSearch,  setShowSearch] = useState(false);

  const [bulkMode,    setBulkMode]   = useState(false);
  const [bulkSel,     setBulkSel]    = useState([]);

  /* modals */
  const [showUpload,    setShowUpload]    = useState(false);
  const [showFilter,    setShowFilter]    = useState(false);
  const [showDetail,    setShowDetail]    = useState(false);
  const [showEdit,      setShowEdit]      = useState(false);
  const [showVersions,  setShowVersions]  = useState(false);
  const [showAddVer,    setShowAddVer]    = useState(false);
  const [showComment,   setShowComment]   = useState(false);
  const [showReject,    setShowReject]    = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [showShareTrail,setShowShareTrail]= useState(false);
  const [showSort,      setShowSort]      = useState(false);
  const [showReview,    setShowReview]    = useState(false);
  /* Turn 1 — Audit + Preview */
  const [showAudit,     setShowAudit]     = useState(false);
  const [showPreview,   setShowPreview]   = useState(false);
  const [reviewNote,    setReviewNote]    = useState('');
  const [isUploading,   setIsUploading]   = useState(false);

  /* forms */
  const [selDoc,    setSelDoc]    = useState(null);
  const [upForm,    setUpForm]    = useState(defaultUploadForm());
  const [editForm,  setEditForm]  = useState({});
  const [newComment,setNewCmt]    = useState('');
  const [rejectRsn, setRejectRsn] = useState('');
  const [verNote,   setVerNote]   = useState('');
  const [newRem,    setNewRem]    = useState({ date:'', note:'' });
  const [newTag,    setNewTag]    = useState('');

  /* ── init ── */
  useEffect(() => {
    init();
    const bh = BackHandler.addEventListener('hardwareBackPress', handleBack);
    return () => bh.remove();
  }, []);

  const handleBack = useCallback(() => {
    if (showDetail || showUpload || showFilter || showEdit || showVersions ||
        showAddVer || showComment || showReject || showAnalytics || showReminders ||
        showShareTrail || showSort || showAudit || showPreview) {
      setShowDetail(false); setShowUpload(false); setShowFilter(false);
      setShowEdit(false); setShowVersions(false); setShowAddVer(false);
      setShowComment(false); setShowReject(false); setShowAnalytics(false);
      setShowReminders(false); setShowShareTrail(false); setShowSort(false);
      setShowAudit(false); setShowPreview(false);
      return true;
    }
    if (bulkMode) { setBulkMode(false); setBulkSel([]); return true; }
    navigation?.goBack();
    return true;
  }, [showDetail, showUpload, showFilter, showEdit, showVersions, showAddVer,
      showComment, showReject, showAnalytics, showReminders, showShareTrail, showSort,
      showAudit, showPreview, bulkMode]);

  const init = async () => {
    setIsLoading(true);
    try {
      // ── Resolve company/business ID ─────────────────────────────────
      const bizRaw = await AsyncStorage.getItem('company_id')
                  || await AsyncStorage.getItem('company_id');
      const cid = bizRaw ? parseInt(bizRaw) : null;
      setCompanyId(cid);

      // ── Load company profile for logo ───────────────────────────────
      try {
        const profileRaw = await AsyncStorage.getItem('company_profile');
        if (profileRaw) {
          const p = JSON.parse(profileRaw);
          const logo = p.logo || p.logo_url || p.company_logo || p.banner_image_url;
          if (logo && typeof logo === 'string' && logo.length > 0) {
            if (logo && (logo.startsWith('http') || logo.startsWith('data:'))) {
              setCompanyLogo(logo);
            } else if (logo.startsWith('/')) {
        if (logo && (logo && (logo.startsWith('http') || logo.startsWith('data:')))) setCompanyLogo(logo); else if (logo) setCompanyLogo(API_BASE + logo);
            }
            // else: skip invalid/unexpected format
          }
          if (p.company_name || p.name) setCompanyName(p.company_name || p.name);
        }
      } catch {}

      // ── Per-company cache key ───────────────────────────────────────
      const cacheKey = cid ? `docs_v3_${cid}` : 'docs_v3';

      const raw = await AsyncStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) { setDocuments(parsed); setIsLoading(false); }
        else { setDocuments([]); setIsLoading(false); }
      } else {
        setDocuments([]);
        setIsLoading(false);
      }

      // ── Background API sync ─────────────────────────────────────────
      const params = cid ? { company_id: cid } : {};
      api.get(EP.docs, { params }).then(r => {
        if (r.data?.success && Array.isArray(r.data.data) && r.data.data.length) {
          // Map real DB columns → local doc shape
          const docs = r.data.data.filter(Boolean).map(d => ({
            ...d,
            // normalise field names
            name:           d.name      || d.title || 'Untitled',
            type:           d.file_type || d.type  || 'pdf',
            size:           d.size_formatted || d.size || '0 KB',
            sizeBytes:      parseInt(d.size_bytes || d.sizeBytes || 0),
            status:         d.status    || 'active',
            accessLevel:    d.access_level  || d.accessLevel  || 'internal',
            approvalStatus: d.approval_status || d.approvalStatus || 'pending',
            approvedBy:     d.approved_by || d.approvedBy || null,
            approvalDate:   d.approval_date || d.approvalDate || null,
            rejectionReason:d.rejection_reason || d.rejectionReason || null,
            createdDate:    d.created_date  ? d.created_date.split('T')[0]  : d.createdDate  || '',
            modifiedDate:   d.modified_date ? d.modified_date.split('T')[0] : d.modifiedDate || '',
            expiryDate:     d.expiry_date   ? d.expiry_date.split('T')[0]   : d.expiryDate   || null,
            reviewDate:     d.review_date   ? d.review_date.split('T')[0]   : d.reviewDate   || null,
            viewCount:      parseInt(d.view_count    || d.viewCount    || 0),
            downloadCount:  parseInt(d.download_count|| d.downloadCount|| 0),
            file_url:       d.location || d.file_url || d.file_path || null,
            tags:           Array.isArray(d.tags) ? d.tags : [],
            shareTrail:     d.shareTrail   || [],
            comments:       d.comments     || [],
            reminders:      d.reminders    || [],
            versions:       d.versions     || [{ version: d.version||'1.0', date: d.created_date||d.createdDate||'', author: d.author||'', changes:'Initial', sizeBytes:0 }],
          }));
          setDocuments(docs);
          AsyncStorage.setItem(cacheKey, JSON.stringify(docs));
        }
      }).catch(() => {});

      // Background analytics (non-blocking)
      Promise.allSettled([
        api.get(EP.analytics,       { params }),
        api.get(EP.analyticsByCat,  { params }),
        api.get(EP.analyticsByType, { params }),
        api.get(EP.analyticsByAccess, { params }),
        api.get(EP.mostViewed,      { params }),
        api.get(EP.mostDownloaded,  { params }),
        api.get(EP.expiringDocs,    { params }),
        api.get(EP.tagsList),
        api.get(EP.categories),
        api.get(EP.types),
        api.get(EP.accessLevels),
      ]).catch(() => {});
    } catch { setDocuments([]); setIsLoading(false); }
  };

  const persist = useCallback(async (docs) => {
    setDocuments(docs);
    const cacheKey = companyId ? `docs_v3_${companyId}` : 'docs_v3';
    await AsyncStorage.setItem(cacheKey, JSON.stringify(docs));
  }, [companyId]);

  /* ── derived data ── */
  const analytics = useMemo(() => {
    const now  = Date.now();
    const in60 = now + 60*86400000;
    return {
      total:      documents.length,
      active:     documents.filter(d => d.status === 'active').length,
      draft:      documents.filter(d => d.status === 'draft').length,
      archived:   documents.filter(d => d.status === 'archived').length,
      pending:    documents.filter(d => d.approvalStatus === 'pending').length,
      expiring:   documents.filter(d => d.expiryDate && new Date(d.expiryDate) <= in60 && new Date(d.expiryDate) >= now).length,
      expired:    documents.filter(d => d.expiryDate && new Date(d.expiryDate) < now).length,
      pinned:     documents.filter(d => d.pinned).length,
      favourites: documents.filter(d => d.favourite).length,
      locked:     documents.filter(d => d.locked).length,
      totalViews: documents.reduce((a,d) => a+(d.viewCount||0), 0),
      totalDL:    documents.reduce((a,d) => a+(d.downloadCount||0), 0),
      totalSize:  (() => {
        const bytes = documents.reduce((a,d) => a+(d.sizeBytes||0), 0);
        if (bytes >= 1e9) return `${(bytes/1e9).toFixed(1)} GB`;
        if (bytes >= 1e6) return `${(bytes/1e6).toFixed(1)} MB`;
        return `${(bytes/1e3).toFixed(0)} KB`;
      })(),
      byCat:  CATEGORIES.map(c   => ({ ...c,  count: documents.filter(d => d.category    === c.id).length })),
      byType: FILE_TYPES.map(t   => ({ ...t,  count: documents.filter(d => d.type        === t.id).length })),
      byAccess:ACCESS_LEVELS.map(a=>({ ...a,  count: documents.filter(d => d.accessLevel === a.id).length })),
    };
  }, [documents]);

  const listData = useMemo(() => {
    const now  = Date.now();
    const in60 = now + 60*86400000;
    let data = [...documents];

    /* tab */
    switch (activeTab) {
      case 'recent':    data = [...data].sort((a,b) => new Date(b.modifiedDate)-new Date(a.modifiedDate)).slice(0,20); break;
      case 'pending':   data = data.filter(d => d.approvalStatus==='pending'); break;
      case 'expiring':  data = data.filter(d => d.expiryDate && new Date(d.expiryDate)<=in60 && new Date(d.expiryDate)>=now); break;
      case 'expired':   data = data.filter(d => d.expiryDate && new Date(d.expiryDate)<now); break;
      case 'archived':  data = data.filter(d => d.status==='archived'); break;
      case 'draft':     data = data.filter(d => d.status==='draft'); break;
      case 'pinned':    data = data.filter(d => d.pinned); break;
      case 'favourites':data = data.filter(d => d.favourite); break;
    }

    /* search */
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      data = data.filter(d =>
        d.name?.toLowerCase().includes(q) ||
        d.description?.toLowerCase().includes(q) ||
        d.author?.toLowerCase().includes(q) ||
        d.reference?.toLowerCase().includes(q) ||
        d.department?.toLowerCase().includes(q) ||
        d.tags?.some(t => t.toLowerCase().includes(q))
      );
    }

    /* filters */
    if (filters.category      !== 'all') data = data.filter(d => d.category      === filters.category);
    if (filters.type          !== 'all') data = data.filter(d => d.type          === filters.type);
    if (filters.status        !== 'all') data = data.filter(d => d.status        === filters.status);
    if (filters.accessLevel   !== 'all') data = data.filter(d => d.accessLevel   === filters.accessLevel);
    if (filters.approvalStatus!== 'all') data = data.filter(d => d.approvalStatus=== filters.approvalStatus);

    /* sort */
    switch (sortBy) {
      case 'newest':   data.sort((a,b) => new Date(b.modifiedDate) - new Date(a.modifiedDate)); break;
      case 'oldest':   data.sort((a,b) => new Date(a.modifiedDate) - new Date(b.modifiedDate)); break;
      case 'created':  data.sort((a,b) => new Date(b.createdDate)  - new Date(a.createdDate));  break;
      case 'az':       data.sort((a,b) => a.name.localeCompare(b.name)); break;
      case 'za':       data.sort((a,b) => b.name.localeCompare(a.name)); break;
      case 'size_desc':data.sort((a,b) => (b.sizeBytes||0) - (a.sizeBytes||0)); break;
      case 'size_asc': data.sort((a,b) => (a.sizeBytes||0) - (b.sizeBytes||0)); break;
      case 'views':    data.sort((a,b) => b.viewCount - a.viewCount); break;
      case 'downloads':data.sort((a,b) => b.downloadCount - a.downloadCount); break;
      case 'expiry':   data.sort((a,b) => {
        const da = a.expiryDate ? new Date(a.expiryDate) : new Date('9999');
        const db = b.expiryDate ? new Date(b.expiryDate) : new Date('9999');
        return da - db;
      }); break;
    }
    return data;
  }, [documents, activeTab, searchQuery, filters, sortBy]);

  /* ── actions ── */
  const openDetail = useCallback((doc) => {
    const updated = documents.map(d => d.id===doc.id ? {...d, viewCount:(d.viewCount||0)+1} : d);
    persist(updated);
    setSelDoc({ ...doc, viewCount:(doc.viewCount||0)+1 });
    setShowDetail(true);
  }, [documents, persist]);

  const handleDownload = useCallback((doc) => {
    const updated = documents.map(d => d.id===doc.id ? {...d, downloadCount:(d.downloadCount||0)+1} : d);
    persist(updated);
    api.post(EP.download(doc.id)).catch(()=>{});
    Alert.alert('Download', `Preparing "${doc.name}" for download…`);
  }, [documents, persist]);

  const handleShare = useCallback(async (doc) => {
    try {
      await Share.share({ message:`📄 ${doc.name}\nRef: ${doc.reference||'N/A'}\nVersion: v${doc.version}\nLocation: ${doc.location}`, title: doc.name });
      const trail = { sharedWith:'(via device share)', date:todayStr(), by:'James Walker' };
      const updated = documents.map(d => d.id===doc.id ? {...d, shareTrail:[trail,...(d.shareTrail||[])]} : d);
      persist(updated);
      // POST to both /share (share action) and /shares (trail record)
      api.post(EP.share(doc.id)).catch(()=>{});
      api.post(EP.shares(doc.id), { sharedWith:'(via device share)', by:'James Walker', date:todayStr() }).catch(()=>{});
    } catch {}
  }, [documents, persist]);

  const handleUpload = useCallback(async () => {
    if (!upForm.name.trim()) { Alert.alert('Required', 'Please enter a document name.'); return; }
    setIsUploading(true);
    try {
      const now = todayStr();
      const fileSize    = upForm.pickedFile?.size     || '0 KB';
      const fileSizeBytes = upForm.pickedFile?.sizeBytes || 0;

      // ── Build FormData for multipart upload ──────────────────────────
      const formData = new FormData();

      if (upForm.pickedFile?.uri) {
        // Append the actual file
        const fileExt  = upForm.pickedFile.name.split('.').pop() || 'pdf';
        const mimeType = upForm.pickedFile.mimeType || `application/${fileExt}`;
        formData.append('file', {
          uri:  upForm.pickedFile.uri,
          name: upForm.pickedFile.name,
          type: mimeType,
        });
      }

      // Append metadata fields
      formData.append('title',        upForm.name.trim());
      formData.append('category',     upForm.category    || 'general');
      formData.append('type',         upForm.type        || 'pdf');
      formData.append('access_level', upForm.accessLevel || 'internal');
      formData.append('status',       'draft');
      formData.append('approval_status', 'pending');
      if (upForm.description)  formData.append('description',  upForm.description);
      if (upForm.author)       formData.append('author',        upForm.author);
      if (upForm.department)   formData.append('department',    upForm.department);
      if (upForm.reference)    formData.append('reference',     upForm.reference);
      if (upForm.tags)         formData.append('tags',          upForm.tags);
      if (upForm.expiryDate)   formData.append('expiry_date',   upForm.expiryDate);
      if (upForm.reviewDate)   formData.append('review_date',   upForm.reviewDate);
      if (companyId)           formData.append('company_id',    String(companyId));

      // ── Get auth token ───────────────────────────────────────────────
      const token = await AsyncStorage.getItem('auth_token')
                 || await AsyncStorage.getItem('token') || '';

      // ── POST multipart to upload endpoint ───────────────────────────
      const uploadRes = await fetch(`https://api.jobeng.ai/api/docs/upload`, {
        method:  'POST',
        headers: {
          'x-api-key':     '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
          'Authorization': `Bearer ${token}`,
          // Do NOT set Content-Type — fetch sets it automatically with boundary
        },
        body: formData,
      });

      const json = await uploadRes.json();

      if (!uploadRes.ok || !json.success) {
        throw new Error(json.error || `Upload failed (${uploadRes.status})`);
      }

      // ── Map server response to local document shape ──────────────────
      const srv = json.data;
      const doc = {
        id:              `doc_${srv.id}` || makeId(),
        _serverId:       srv.id,
        name:            srv.name        || srv.title      || upForm.name,
        category:        srv.category    || upForm.category,
        type:            srv.file_type   || srv.type       || upForm.type,
        size:            srv.size_formatted || fileSize,
        sizeBytes:       parseInt(srv.size_bytes || fileSizeBytes),
        status:          srv.status      || 'draft',
        accessLevel:     srv.access_level || upForm.accessLevel,
        version:         srv.version     || '1.0',
        createdDate:     srv.created_date  ? srv.created_date.split('T')[0]  : now,
        modifiedDate:    srv.modified_date ? srv.modified_date.split('T')[0] : now,
        expiryDate:      srv.expiry_date   ? srv.expiry_date.split('T')[0]   : upForm.expiryDate  || null,
        reviewDate:      srv.review_date   ? srv.review_date.split('T')[0]   : upForm.reviewDate  || null,
        author:          srv.author        || upForm.author,
        department:      srv.department    || upForm.department,
        reference:       srv.reference     || upForm.reference || srv.document_number,
        description:     srv.description   || upForm.description,
        tags:            Array.isArray(srv.tags) ? srv.tags : (upForm.tags ? upForm.tags.split(',').map(t=>t.trim()).filter(Boolean) : []),
        downloadCount:   0,
        viewCount:       0,
        approvalStatus:  srv.approval_status || 'pending',
        approvedBy:      null,
        approvalDate:    null,
        pinned:          false,
        favourite:       false,
        locked:          false,
        company_id:      companyId,
        company_id:     companyId,
        file_url:        srv.location    || srv.file_path  || null,
        checksum:        srv.checksum    || Math.random().toString(36).slice(2,10),
        location:        srv.location    || srv.file_path  || `/documents/${upForm.category}/${upForm.name}`,
        shareTrail: [], comments: [], reminders: [],
        versions: [{ version:'1.0', date:now, author:upForm.author||'User', changes:'Initial upload', sizeBytes:fileSizeBytes }],
      };

      await persist([doc, ...documents]);
      setUpForm(defaultUploadForm());
      setShowUpload(false);
      Alert.alert('✅ Uploaded', `"${doc.name}" saved successfully.\nRef: ${doc.reference || doc.id}`);

    } catch (err) {
      console.error('[handleUpload]', err);
      Alert.alert('Upload Failed', err.message || 'Could not upload file. Please try again.');
    } finally {
      setIsUploading(false);
    }
  }, [upForm, documents, persist, companyId]);

  const handlePickFile = useCallback(async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        copyToCacheDirectory: true,
        multiple: false,
      });
      if (result.canceled || !result.assets?.length) return;
      const asset = result.assets[0];
      // Auto-detect type from mime
      const mimeMap = {
        'application/pdf': 'pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
        'application/msword': 'docx',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
        'application/vnd.ms-excel': 'xlsx',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',
        'application/zip': 'zip',
        'text/plain': 'txt',
        'video/mp4': 'mp4',
        'image/jpeg': 'img', 'image/png': 'img', 'image/gif': 'img',
      };
      const detectedType = mimeMap[asset.mimeType] || 'pdf';
      const sizeBytes = asset.size || 0;
      const sizeMB = sizeBytes > 1e6
        ? `${(sizeBytes / 1e6).toFixed(1)} MB`
        : sizeBytes > 1e3
        ? `${(sizeBytes / 1e3).toFixed(0)} KB`
        : `${sizeBytes} B`;
      setUpForm(p => ({
        ...p,
        pickedFile: { name: asset.name, uri: asset.uri, size: sizeMB, sizeBytes, mimeType: asset.mimeType },
        name: p.name || asset.name.replace(/\.[^.]+$/, ''), // auto-fill name if empty
        type: detectedType,
      }));
    } catch (err) {
      Alert.alert('Error', 'Could not open file picker: ' + err.message);
    }
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editForm.name?.trim()) { Alert.alert('Required','Document name cannot be empty.'); return; }
    const updated = documents.map(d => d.id===selDoc.id ? {
      ...d, ...editForm, modifiedDate:todayStr(),
      tags: typeof editForm.tags==='string' ? editForm.tags.split(',').map(t=>t.trim()).filter(Boolean) : editForm.tags,
    } : d);
    await persist(updated);
    api.put(EP.doc(selDoc.id), editForm).catch(()=>{});
    setShowEdit(false); setShowDetail(false);
    Alert.alert('Updated','Document saved successfully.');
  }, [editForm, selDoc, documents, persist]);

  const handleApprove = useCallback((doc) => {
    Alert.alert('Approve Document', `Approve "${doc.name}"?`, [
      { text:'Cancel', style:'cancel' },
      { text:'Approve', onPress: async () => {
        const now = todayStr();
        const updated = documents.map(d => d.id===doc.id
          ? { ...d, approvalStatus:'approved', approvedBy:'James Walker', approvalDate:now, status:'active' } : d);
        await persist(updated);
        setSelDoc(prev => prev ? { ...prev, approvalStatus:'approved', approvedBy:'James Walker', approvalDate:now, status:'active' } : prev);
        api.post(EP.approve(doc.id)).catch(()=>{});
        Alert.alert('Approved','Document approved and set to active.');
      }},
    ]);
  }, [documents, persist]);

  const handleReject = useCallback(async () => {
    if (!rejectRsn.trim()) { Alert.alert('Required','Please enter a rejection reason.'); return; }
    const updated = documents.map(d => d.id===selDoc.id
      ? { ...d, approvalStatus:'rejected', rejectionReason:rejectRsn, status:'under_review' } : d);
    await persist(updated);
    setSelDoc(prev => prev ? { ...prev, approvalStatus:'rejected', rejectionReason:rejectRsn } : prev);
    api.post(EP.reject(selDoc.id), { reason:rejectRsn }).catch(()=>{});
    setRejectRsn(''); setShowReject(false);
    Alert.alert('Rejected','Document rejected. Author will be notified.');
  }, [rejectRsn, selDoc, documents, persist]);

  const handleArchive = useCallback((doc) => {
    Alert.alert('Archive', `Archive "${doc.name}"?`, [
      { text:'Cancel', style:'cancel' },
      { text:'Archive', onPress: async () => {
        await persist(documents.map(d => d.id===doc.id ? {...d, status:'archived'} : d));
        api.post(EP.archive(doc.id)).catch(()=>{});
        setShowDetail(false);
      }},
    ]);
  }, [documents, persist]);

  const handleRestore = useCallback((doc) => {
    Alert.alert('Restore', `Restore "${doc.name}" to active?`, [
      { text:'Cancel', style:'cancel' },
      { text:'Restore', onPress: async () => {
        await persist(documents.map(d => d.id===doc.id ? {...d, status:'active'} : d));
        api.post(EP.restore(doc.id)).catch(()=>{});
        setShowDetail(false);
        Alert.alert('Restored','Document is now active.');
      }},
    ]);
  }, [documents, persist]);

  const handleDelete = useCallback((doc) => {
    Alert.alert('Delete Document', `Permanently delete "${doc.name}"?\n\nThis action cannot be undone.`, [
      { text:'Cancel', style:'cancel' },
      { text:'Delete', style:'destructive', onPress: async () => {
        await persist(documents.filter(d => d.id!==doc.id));
        api.delete(EP.doc(doc.id)).catch(()=>{});
        setShowDetail(false);
      }},
    ]);
  }, [documents, persist]);

  const handleToggleFavourite = useCallback((doc) => {
    const updated = documents.map(d => d.id===doc.id ? {...d, favourite:!d.favourite} : d);
    persist(updated);
    setSelDoc(prev => prev ? {...prev, favourite:!prev.favourite} : prev);
    api.post(EP.favourite(doc.id), { favourite:!doc.favourite }).catch(()=>{});
  }, [documents, persist]);

  const handleTogglePin = useCallback((doc) => {
    const updated = documents.map(d => d.id===doc.id ? {...d, pinned:!d.pinned} : d);
    persist(updated);
    setSelDoc(prev => prev ? {...prev, pinned:!prev.pinned} : prev);
    api.post(EP.pin(doc.id), { pinned:!doc.pinned }).catch(()=>{});
  }, [documents, persist]);

  const handleToggleLock = useCallback((doc) => {
    Alert.alert(doc.locked ? 'Unlock Document' : 'Lock Document',
      doc.locked ? 'Unlock this document for editing?' : 'Lock this document to prevent editing?',
      [
        { text:'Cancel', style:'cancel' },
        { text: doc.locked ? 'Unlock' : 'Lock', onPress: () => {
          const updated = documents.map(d => d.id===doc.id ? {...d, locked:!d.locked} : d);
          persist(updated);
          setSelDoc(prev => prev ? {...prev, locked:!prev.locked} : prev);
          api.post(EP.lock(doc.id), { locked:!doc.locked }).catch(()=>{});
        }},
      ]
    );
  }, [documents, persist]);

  // ─── PRINT ───────────────────────────────────────────────────────────────
  const handlePrint = useCallback(async (doc) => {
    try {
      const cat    = getCat(doc.category);
      const type   = getType(doc.type);
      const status = getStatus(doc.status);
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:Arial,sans-serif;color:#1e293b;padding:32px;}
  .header{background:linear-gradient(135deg,#0e4e96,#1565c0);color:#fff;padding:28px 32px;border-radius:12px;margin-bottom:24px;}
  .header h1{font-size:22px;font-weight:700;margin-bottom:6px;}
  .header p{font-size:13px;opacity:0.85;}
  .badge{display:inline-block;background:rgba(255,255,255,0.2);border-radius:20px;padding:4px 14px;font-size:12px;margin-top:8px;font-weight:600;}
  .section{background:#f8fafc;border-radius:10px;padding:16px 20px;margin-bottom:16px;border:1px solid #e2e8f0;}
  .section h2{font-size:12px;font-weight:700;color:#0e4e96;text-transform:uppercase;letter-spacing:1px;margin-bottom:12px;}
  .row{display:flex;justify-content:space-between;padding:7px 0;border-bottom:1px solid #f1f5f9;font-size:13px;}
  .row:last-child{border-bottom:none;}
  .label{color:#64748b;} .value{font-weight:600;color:#1e293b;text-align:right;}
  .desc{font-size:13px;color:#475569;line-height:1.7;}
  .tags{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px;}
  .tag{background:#eff6ff;color:#3b82f6;border-radius:20px;padding:4px 12px;font-size:11px;font-weight:600;}
  .footer{text-align:center;font-size:11px;color:#94a3b8;margin-top:28px;padding-top:16px;border-top:1px solid #e2e8f0;}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
</style></head><body>
<div class="header">
  <h1>${doc.name}</h1>
  <p>${doc.reference ? `Ref: ${doc.reference}` : ''} ${doc.author ? `· ${doc.author}` : ''}</p>
  <span class="badge">${cat.label} · ${type.label} · v${doc.version||'1.0'}</span>
  <span class="badge" style="margin-left:6px;">${status.label}</span>
</div>

<div class="section"><h2>Document Information</h2>
  ${[
    ['File Type',   type.label],
    ['File Size',   doc.size],
    ['Version',     `v${doc.version}`],
    ['Author',      doc.author],
    ['Department',  doc.department],
    ['Reference',   doc.reference],
    ['Access Level',getCat(doc.accessLevel)?.label || doc.accessLevel],
    ['Status',      status.label],
    ['Approval',    (doc.approvalStatus||'pending').toUpperCase()],
    ['Approved By', doc.approvedBy],
    ['Location',    doc.location],
  ].filter(([,v])=>v).map(([l,v])=>`<div class="row"><span class="label">${l}</span><span class="value">${v}</span></div>`).join('')}
</div>

<div class="section"><h2>Dates</h2>
  ${[
    ['Created',  doc.createdDate],
    ['Modified', doc.modifiedDate],
    ['Expiry',   doc.expiryDate],
    ['Review',   doc.reviewDate],
  ].filter(([,v])=>v).map(([l,v])=>`<div class="row"><span class="label">${l}</span><span class="value">${v}</span></div>`).join('')}
</div>

${doc.description ? `<div class="section"><h2>Description</h2><p class="desc">${doc.description}</p></div>` : ''}

${doc.tags?.length ? `<div class="section"><h2>Tags</h2><div class="tags">${doc.tags.map(t=>`<span class="tag">#${t}</span>`).join('')}</div></div>` : ''}

<div class="footer">
  Generated by JOBENG Documents Control · ${new Date().toLocaleDateString('en-GB',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}
</div>
</body></html>`;
      await Print.printAsync({ html });
    } catch (e) {
      Alert.alert('Print Error', e.message);
    }
  }, []);

  // ─── EXPORT PDF ──────────────────────────────────────────────────────────
  const handleExportPDF = useCallback(async (doc) => {
    try {
      const cat    = getCat(doc.category);
      const type   = getType(doc.type);
      const status = getStatus(doc.status);
      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<style>
  *{box-sizing:border-box;margin:0;padding:0;}
  body{font-family:Arial,sans-serif;color:#1e293b;}
  .header{background:linear-gradient(135deg,#0e4e96,#1565c0);color:#fff;padding:28px 32px;}
  .header h1{font-size:22px;font-weight:700;margin-bottom:4px;}
  .header p{font-size:13px;opacity:0.85;}
  .badge{display:inline-block;background:rgba(255,255,255,0.2);border-radius:20px;padding:4px 14px;font-size:11px;margin-top:8px;font-weight:600;}
  .body{padding:24px 32px;}
  .section{background:#f8fafc;border-radius:10px;padding:16px;margin-bottom:16px;border:1px solid #e2e8f0;}
  .section h2{font-size:11px;font-weight:700;color:#0e4e96;text-transform:uppercase;letter-spacing:1px;margin-bottom:12px;}
  .row{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid #f1f5f9;font-size:13px;}
  .row:last-child{border-bottom:none;}
  .label{color:#64748b;} .value{font-weight:600;color:#1e293b;}
  .desc{font-size:13px;color:#475569;line-height:1.7;}
  .tags{display:flex;flex-wrap:wrap;gap:6px;margin-top:8px;}
  .tag{background:#eff6ff;color:#3b82f6;border-radius:20px;padding:3px 10px;font-size:11px;font-weight:600;}
  .footer{background:#f8fafc;padding:14px 32px;text-align:center;font-size:11px;color:#94a3b8;border-top:1px solid #e2e8f0;}
  @media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
</style></head><body>
<div class="header">
  <h1>${doc.name}</h1>
  <p>${doc.reference ? `Ref: ${doc.reference}` : ''} ${doc.author ? `· ${doc.author}` : ''}</p>
  <span class="badge">${cat.label} · ${type.label} · v${doc.version||'1.0'}</span>
  <span class="badge" style="margin-left:6px;">${status.label}</span>
</div>
<div class="body">
<div class="section"><h2>Document Information</h2>
  ${[
    ['File Type',   type.label],
    ['File Size',   doc.size],
    ['Version',     `v${doc.version}`],
    ['Author',      doc.author],
    ['Department',  doc.department],
    ['Reference',   doc.reference],
    ['Access Level',doc.accessLevel],
    ['Status',      status.label],
    ['Approval',    (doc.approvalStatus||'pending').toUpperCase()],
    ['Approved By', doc.approvedBy],
  ].filter(([,v])=>v).map(([l,v])=>`<div class="row"><span class="label">${l}</span><span class="value">${v}</span></div>`).join('')}
</div>
<div class="section"><h2>Dates</h2>
  ${[['Created',doc.createdDate],['Modified',doc.modifiedDate],['Expiry',doc.expiryDate],['Review',doc.reviewDate]].filter(([,v])=>v).map(([l,v])=>`<div class="row"><span class="label">${l}</span><span class="value">${v}</span></div>`).join('')}
</div>
${doc.description?`<div class="section"><h2>Description</h2><p class="desc">${doc.description}</p></div>`:''}
${doc.tags?.length?`<div class="section"><h2>Tags</h2><div class="tags">${doc.tags.map(t=>`<span class="tag">#${t}</span>`).join('')}</div></div>`:''}
${doc.versions?.length?`<div class="section"><h2>Version History</h2>${doc.versions.map(v=>`<div class="row"><span class="label">v${v.version} · ${v.date}</span><span class="value">${v.author}</span></div>`).join('')}</div>`:''}
</div>
<div class="footer">Generated by JOBENG · ${new Date().toLocaleDateString('en-GB',{weekday:'long',year:'numeric',month:'long',day:'numeric'})}</div>
</body></html>`;
      const { uri } = await Print.printToFileAsync({ html, base64: false });
      await Sharing.shareAsync(uri, { mimeType:'application/pdf', dialogTitle:`${doc.name} – Document Sheet` });
    } catch (e) {
      Alert.alert('Export Error', e.message);
    }
  }, []);

  // ─── VIEW FILE (open in browser/viewer) ──────────────────────────────────
  const handleViewFile = useCallback(async (doc) => {
    const url = doc.file_url || doc.location;
    if (!url || url.startsWith('/documents/')) {
      Alert.alert('Not Available', 'This document has no accessible file URL yet. Upload the file to enable viewing.');
      return;
    }
    try {
      await Linking.openURL(url);
    } catch (e) {
      Alert.alert('Error', e.message);
    }
  }, []);

  // ─── SUBMIT REVIEW ───────────────────────────────────────────────────────
  const handleReviewSubmit = useCallback(async () => {
    if (!selDoc) return;
    const updated = documents.map(d => d.id === selDoc.id ? {
      ...d,
      status: 'under_review',
      reviewNote: reviewNote.trim(),
      modifiedDate: todayStr(),
    } : d);
    await persist(updated);
    setSelDoc(prev => prev ? { ...prev, status:'under_review', reviewNote:reviewNote.trim() } : prev);
    api.put(EP.doc(selDoc.id), { status:'under_review', notes: reviewNote }).catch(()=>{});
    setShowReview(false);
    setReviewNote('');
    Alert.alert('Review Requested', `"${selDoc.name}" has been submitted for review.`);
  }, [selDoc, documents, persist, reviewNote]);

  const handleDuplicate = useCallback((doc) => {
    const now = todayStr();
    const copy = {
      ...doc, id:makeId(),
      name:`Copy of ${doc.name}`,
      createdDate:now, modifiedDate:now,
      approvalStatus:'pending', approvedBy:null, approvalDate:null, rejectionReason:undefined,
      status:'draft', downloadCount:0, viewCount:0, pinned:false, favourite:false,
      shareTrail:[],
      versions:[{ version:'1.0', date:now, author:'James Walker', changes:`Duplicated from ${doc.name}`, sizeBytes:doc.sizeBytes||0 }],
    };
    persist([copy,...documents]);
    api.post(EP.duplicate(doc.id)).then(r => {
      if (r.data?.data?.id) {
        // server returned a new ID – update local copy
        const withServerId = [copy,...documents].map(d => d.id===copy.id ? {...d, id:r.data.data.id} : d);
        persist(withServerId);
      }
    }).catch(()=>{});
    setShowDetail(false);
    Alert.alert('Duplicated',`"${copy.name}" created as a draft.`);
  }, [documents, persist]);

  const handleAddComment = useCallback(async () => {
    if (!newComment.trim()) return;
    const cmt = { id:makeId('c'), author:'James Walker', avatar:'J', text:newComment, date:nowISO(), resolved:false };
    const updated = documents.map(d => d.id===selDoc.id ? {...d, comments:[...(d.comments||[]), cmt]} : d);
    await persist(updated);
    setSelDoc(prev => prev ? {...prev, comments:[...(prev.comments||[]), cmt]} : prev);
    api.post(EP.comments(selDoc.id), cmt).catch(()=>{});
    setNewCmt(''); setShowComment(false);
  }, [newComment, selDoc, documents, persist]);

  const handleResolveComment = useCallback((cmt) => {
    const updated = documents.map(d => d.id===selDoc.id
      ? { ...d, comments:(d.comments||[]).map(c => c.id===cmt.id ? {...c, resolved:!c.resolved} : c) } : d);
    persist(updated);
    setSelDoc(prev => prev ? { ...prev, comments:(prev.comments||[]).map(c => c.id===cmt.id ? {...c, resolved:!c.resolved} : c) } : prev);
    api.put(EP.resolveComment(selDoc.id, cmt.id), { resolved:!cmt.resolved }).catch(()=>{});
  }, [selDoc, documents, persist]);

  const handleDeleteComment = useCallback((cmt) => {
    Alert.alert('Delete Comment', 'Delete this comment?', [
      { text:'Cancel', style:'cancel' },
      { text:'Delete', style:'destructive', onPress: () => {
        const updated = documents.map(d => d.id===selDoc.id
          ? { ...d, comments:(d.comments||[]).filter(c => c.id!==cmt.id) } : d);
        persist(updated);
        setSelDoc(prev => prev ? { ...prev, comments:(prev.comments||[]).filter(c => c.id!==cmt.id) } : prev);
        api.delete(EP.deleteComment(selDoc.id, cmt.id)).catch(()=>{});
      }},
    ]);
  }, [selDoc, documents, persist]);

  const handleAddTag = useCallback((tag) => {
    const trimmed = tag.trim().toLowerCase().replace(/\s+/g,'-');
    if (!trimmed || selDoc?.tags?.includes(trimmed)) return;
    const updated = documents.map(d => d.id===selDoc.id
      ? { ...d, tags:[...(d.tags||[]), trimmed] } : d);
    persist(updated);
    setSelDoc(prev => prev ? { ...prev, tags:[...(prev.tags||[]), trimmed] } : prev);
    api.post(EP.docTags(selDoc.id), { tag:trimmed }).catch(()=>{});
  }, [selDoc, documents, persist]);

  const handleRemoveTag = useCallback((tag) => {
    const updated = documents.map(d => d.id===selDoc.id
      ? { ...d, tags:(d.tags||[]).filter(t => t!==tag) } : d);
    persist(updated);
    setSelDoc(prev => prev ? { ...prev, tags:(prev.tags||[]).filter(t => t!==tag) } : prev);
    api.delete(EP.docTag(selDoc.id, tag)).catch(()=>{});
  }, [selDoc, documents, persist]);

  const handleAddVersion = useCallback(async () => {
    if (!verNote.trim()) { Alert.alert('Required','Please describe the changes.'); return; }
    const [major, minor] = (selDoc.version||'1.0').split('.').map(Number);
    const newVer = `${major}.${(minor||0)+1}`;
    const entry = { version:newVer, date:todayStr(), author:'James Walker', changes:verNote, sizeBytes:selDoc.sizeBytes||0 };
    const updated = documents.map(d => d.id===selDoc.id
      ? { ...d, version:newVer, modifiedDate:todayStr(), versions:[entry,...(d.versions||[])] } : d);
    await persist(updated);
    setSelDoc(prev => prev ? { ...prev, version:newVer, versions:[entry,...(prev.versions||[])] } : prev);
    api.post(EP.versions(selDoc.id), entry).then(() => {
      // Optionally fetch the saved version back for confirmation
      api.get(EP.version(selDoc.id, newVer)).catch(()=>{});
    }).catch(()=>{});
    setVerNote(''); setShowAddVer(false);
    Alert.alert('Version Added',`Document bumped to v${newVer}.`);
  }, [verNote, selDoc, documents, persist]);

  const handleAddReminder = useCallback(async () => {
    if (!newRem.date || !newRem.note.trim()) { Alert.alert('Required','Please enter a date and note.'); return; }
    const rem = { id:makeId('r'), ...newRem, triggered:false };
    const updated = documents.map(d => d.id===selDoc.id ? {...d, reminders:[...(d.reminders||[]), rem]} : d);
    await persist(updated);
    setSelDoc(prev => prev ? {...prev, reminders:[...(prev.reminders||[]), rem]} : prev);
    api.post(EP.reminders(selDoc.id), rem).catch(()=>{});
    setNewRem({ date:'', note:'' }); setShowReminders(false);
    Alert.alert('Reminder Set',`Reminder set for ${fmtDate(rem.date)}.`);
  }, [newRem, selDoc, documents, persist]);

  const handleDeleteReminder = useCallback((remId) => {
    const updated = documents.map(d => d.id===selDoc.id
      ? { ...d, reminders:(d.reminders||[]).filter(r => r.id!==remId) } : d);
    persist(updated);
    setSelDoc(prev => prev ? { ...prev, reminders:(prev.reminders||[]).filter(r => r.id!==remId) } : prev);
    api.delete(EP.reminder(selDoc.id, remId)).catch(()=>{});
  }, [selDoc, documents, persist]);

  /* bulk */
  const toggleBulkSel = useCallback((id) =>
    setBulkSel(p => p.includes(id) ? p.filter(i=>i!==id) : [...p, id]), []);

  const bulkArchive = useCallback(async () => {
    const ids = new Set(bulkSel);
    await persist(documents.map(d => ids.has(d.id) ? {...d, status:'archived'} : d));
    setBulkSel([]); setBulkMode(false);
    Alert.alert('Done',`${ids.size} documents archived.`);
  }, [bulkSel, documents, persist]);

  const bulkDelete = useCallback(() => {
    Alert.alert('Delete', `Permanently delete ${bulkSel.length} document${bulkSel.length!==1?'s':''}?`, [
      { text:'Cancel', style:'cancel' },
      { text:'Delete', style:'destructive', onPress: async () => {
        const ids = new Set(bulkSel);
        await persist(documents.filter(d => !ids.has(d.id)));
        setBulkSel([]); setBulkMode(false);
      }},
    ]);
  }, [bulkSel, documents, persist]);

  const bulkFavourite = useCallback(async () => {
    const ids = new Set(bulkSel);
    await persist(documents.map(d => ids.has(d.id) ? {...d, favourite:true} : d));
    setBulkSel([]); setBulkMode(false);
  }, [bulkSel, documents, persist]);

  const onCardPress = useCallback((item) => {
    if (bulkMode) toggleBulkSel(item.id);
    else openDetail(item);
  }, [bulkMode, toggleBulkSel, openDetail]);

  const onCardLong = useCallback((item) => {
    setBulkMode(true); toggleBulkSel(item.id);
  }, [toggleBulkSel]);

  const renderItem = useCallback(({ item }) => (
    <DocumentCard item={item} viewMode={viewMode} bulkMode={bulkMode}
      bulkSelected={bulkSel} onPress={onCardPress} onLongPress={onCardLong} />
  ), [viewMode, bulkMode, bulkSel, onCardPress, onCardLong]);

  /* ── tabs config ── */
  const TABS = useMemo(() => [
    { id:'all',       label:'All',        icon:'file-multiple',          badge:analytics.total },
    { id:'recent',    label:'Recent',     icon:'clock-outline',          badge:0 },
    { id:'draft',     label:'Draft',      icon:'pencil-outline',         badge:analytics.draft },
    { id:'pending',   label:'Pending',    icon:'clock-alert-outline',    badge:analytics.pending },
    { id:'expiring',  label:'Expiring',   icon:'calendar-alert',         badge:analytics.expiring },
    { id:'expired',   label:'Expired',    icon:'calendar-remove',        badge:analytics.expired },
    { id:'archived',  label:'Archived',   icon:'archive-outline',        badge:analytics.archived },
    { id:'pinned',    label:'Pinned',     icon:'pin-outline',            badge:analytics.pinned },
    { id:'favourites',label:'Favourites', icon:'heart-outline',          badge:analytics.favourites },
  ], [analytics]);

  /* ── LIST HEADER (memoised) ── */
  const ListHeader = useCallback(() => (
    <>
      {/* ── HEADER GRADIENT ── */}
      <LinearGradient colors={[C.primary,'#1565c0']} style={s.header}>
        <View style={s.hRow}>
          <TouchableOpacity style={s.hBtn} onPress={() => navigation?.goBack()}>
            <Ionicons name="arrow-back" size={22} color="#fff" />
          </TouchableOpacity>

          {/* Company logo */}
          {companyLogo ? (
            <Image
              source={{ uri: companyLogo || undefined }}
              style={s.hLogo}
              resizeMode="contain"
            />
          ) : (
            <View style={s.hLogoFallback}>
              <MaterialCommunityIcons name="office-building" size={18} color="rgba(255,255,255,0.8)" />
            </View>
          )}

          <View style={{ flex:1, marginHorizontal:8 }}>
            <Text style={s.hTitle}>Documents Control</Text>
            <Text style={s.hSub} numberOfLines={1}>
              {companyName || ''}{companyName ? ' · ' : ''}{analytics.total} docs · {analytics.totalSize} · {analytics.pending} pending
            </Text>
          </View>
          <View style={{ flexDirection:'row', gap:6 }}>
            <TouchableOpacity style={s.hBtn} onPress={() => setShowSearch(p=>!p)}>
              <Ionicons name={showSearch ? 'close' : 'search'} size={20} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity style={s.hBtn} onPress={() => setShowAnalytics(true)}>
              <MaterialCommunityIcons name="chart-bar" size={20} color="#fff" />
            </TouchableOpacity>
            <TouchableOpacity style={s.hBtn} onPress={() => setShowFilter(true)}>
              <MaterialCommunityIcons name="filter-variant" size={20} color="#fff" />
            </TouchableOpacity>
          </View>
        </View>

        {/* Stats bar */}
        <View style={s.statsBar}>
          {[
            { l:'Active',   v:analytics.active,    icon:'check-circle-outline',  c:'#4ade80' },
            { l:'Pending',  v:analytics.pending,   icon:'clock-alert-outline',   c:'#fbbf24' },
            { l:'Expiring', v:analytics.expiring,  icon:'calendar-alert',        c:'#fb923c' },
            { l:'Archived', v:analytics.archived,  icon:'archive-outline',       c:'rgba(255,255,255,0.6)' },
          ].map(st => (
            <View key={st.l} style={s.statItem}>
              <MaterialCommunityIcons name={st.icon} size={15} color={st.c} />
              <Text style={s.statVal}>{st.v}</Text>
              <Text style={s.statLbl}>{st.l}</Text>
            </View>
          ))}
        </View>

        {/* Search bar */}
        {showSearch && (
          <View style={s.searchBar}>
            <Ionicons name="search" size={17} color={C.light} />
            <TextInput
              style={s.searchInput} autoFocus
              placeholder="Search name, author, ref, tag…"
              placeholderTextColor={C.light}
              value={searchQuery}
              onChangeText={setSearch}
            />
            {searchQuery ? <TouchableOpacity onPress={() => setSearch('')}><Ionicons name="close-circle" size={17} color={C.light} /></TouchableOpacity> : null}
          </View>
        )}
      </LinearGradient>

      {/* ── TABS ── */}
      <View style={{ backgroundColor:'#fff', borderBottomWidth:1, borderBottomColor:C.border }}>
        <FlatList
          horizontal data={TABS} keyExtractor={t=>t.id}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ paddingHorizontal:4 }}
          renderItem={({ item:t }) => (
            <TouchableOpacity
              style={[s.tab, activeTab===t.id && s.tabActive]}
              onPress={() => setActiveTab(t.id)}
            >
              <MaterialCommunityIcons name={t.icon} size={14} color={activeTab===t.id ? C.primary : C.mid} />
              <Text style={[s.tabTxt, activeTab===t.id && s.tabTxtActive]}>{t.label}</Text>
              {t.badge > 0 && (
                <View style={[s.tabBadge, activeTab===t.id && s.tabBadgeActive]}>
                  <Text style={[s.tabBadgeTxt, activeTab===t.id && { color:C.primary }]}>{t.badge}</Text>
                </View>
              )}
            </TouchableOpacity>
          )}
        />
      </View>

      {/* ── ACTION BAR ── */}
      <View style={s.actionBar}>
        <TouchableOpacity onPress={() => setShowUpload(true)}>
          <LinearGradient colors={[C.primary,'#1565c0']} style={s.uploadBtn}>
            <Ionicons name="add" size={18} color="#fff" />
            <Text style={s.uploadBtnTxt}>Upload</Text>
          </LinearGradient>
        </TouchableOpacity>
        <View style={{ flexDirection:'row', gap:6 }}>
          <TouchableOpacity style={s.iconBtn} onPress={() => setShowSort(true)}>
            <MaterialCommunityIcons name="sort-variant" size={18} color={C.primary} />
          </TouchableOpacity>
          <TouchableOpacity style={s.iconBtn} onPress={() => setView(v => v==='list'?'grid':'list')}>
            <MaterialCommunityIcons name={viewMode==='list' ? 'view-grid-outline' : 'view-list'} size={18} color={C.primary} />
          </TouchableOpacity>
          <TouchableOpacity style={[s.iconBtn, bulkMode && { backgroundColor:`${C.danger}18` }]} onPress={() => { setBulkMode(b=>!b); setBulkSel([]); }}>
            <MaterialCommunityIcons name={bulkMode ? 'close-box-outline' : 'checkbox-multiple-outline'} size={18} color={bulkMode ? C.danger : C.primary} />
          </TouchableOpacity>
        </View>
      </View>

      {/* ── BULK BAR ── */}
      {bulkMode && bulkSel.length > 0 && (
        <View style={s.bulkBar}>
          <Text style={s.bulkCount}>{bulkSel.length} selected</Text>
          <TouchableOpacity style={s.bulkBtn} onPress={bulkFavourite}>
            <Ionicons name="heart-outline" size={17} color={C.danger} />
            <Text style={[s.bulkBtnTxt, { color:C.danger }]}>Favourite</Text>
          </TouchableOpacity>
          <TouchableOpacity style={s.bulkBtn} onPress={bulkArchive}>
            <MaterialCommunityIcons name="archive-outline" size={17} color={C.warning} />
            <Text style={[s.bulkBtnTxt, { color:C.warning }]}>Archive</Text>
          </TouchableOpacity>
          <TouchableOpacity style={s.bulkBtn} onPress={bulkDelete}>
            <MaterialCommunityIcons name="delete-outline" size={17} color={C.danger} />
            <Text style={[s.bulkBtnTxt, { color:C.danger }]}>Delete</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={() => { setBulkSel([]); setBulkMode(false); }}>
            <Ionicons name="close" size={20} color={C.mid} />
          </TouchableOpacity>
        </View>
      )}

      {/* Result count + active filter chips */}
      <View style={s.resultBar}>
        <Text style={s.resultCount}>{listData.length} document{listData.length!==1?'s':''}</Text>
        <View style={{ flexDirection:'row', gap:6 }}>
          {Object.entries(filters).filter(([,v])=>v!=='all').map(([k,v]) => (
            <TouchableOpacity key={k} style={s.activeFilterChip} onPress={() => setFilters(p=>({...p,[k]:'all'}))}>
              <Text style={s.activeFilterChipTxt}>{v}</Text>
              <Ionicons name="close" size={10} color={C.primary} />
            </TouchableOpacity>
          ))}
        </View>
      </View>
    </>
  ), [activeTab, analytics, searchQuery, showSearch, viewMode, bulkMode, bulkSel, TABS, filters, listData.length, sortBy]);

  const ListEmpty = useCallback(() => (
    <View style={s.empty}>
      <MaterialCommunityIcons name="file-document-outline" size={72} color={C.border} />
      <Text style={s.emptyTitle}>No documents found</Text>
      <Text style={s.emptySub}>{searchQuery ? 'Try a different search or clear filters.' : 'Tap Upload to add your first document.'}</Text>
      {Object.values(filters).some(v=>v!=='all') && (
        <TouchableOpacity style={s.emptyAction} onPress={() => setFilters({ category:'all', type:'all', status:'all', accessLevel:'all', approvalStatus:'all' })}>
          <Text style={s.emptyActionTxt}>Clear all filters</Text>
        </TouchableOpacity>
      )}
    </View>
  ), [searchQuery, filters]);

  // ─── UPLOAD MODAL ─────────────────────────────────────────────────────────
  const renderUploadModal = () => (
    <Modal visible={showUpload} animationType="slide" onRequestClose={() => setShowUpload(false)}>
      <SafeAreaView style={s.modalFull}>
        <LinearGradient colors={[C.primary,'#1565c0']} style={s.modalHd}>
          <TouchableOpacity onPress={() => setShowUpload(false)}><Ionicons name="close" size={24} color="#fff" /></TouchableOpacity>
          <Text style={s.modalTitle}>Upload Document</Text>
          <View style={{ width:24 }} />
        </LinearGradient>
        <FlatList data={[1]} keyExtractor={()=>'u'} renderItem={() => (
          <View style={s.formBody}>
            {/* Name */}
            <Text style={s.fLabel}>Document Name *</Text>
            <TextInput style={s.input} placeholder="e.g. Site A Safety Risk Assessment" placeholderTextColor={C.light} value={upForm.name} onChangeText={v=>setUpForm(p=>({...p,name:v}))} />

            <View style={s.fRow}>
              <View style={{ flex:1, marginRight:8 }}>
                <Text style={s.fLabel}>Author</Text>
                <TextInput style={s.input} placeholder="Name" placeholderTextColor={C.light} value={upForm.author} onChangeText={v=>setUpForm(p=>({...p,author:v}))} />
              </View>
              <View style={{ flex:1, marginLeft:8 }}>
                <Text style={s.fLabel}>Department</Text>
                <TextInput style={s.input} placeholder="Department" placeholderTextColor={C.light} value={upForm.department} onChangeText={v=>setUpForm(p=>({...p,department:v}))} />
              </View>
            </View>

            <Text style={s.fLabel}>Reference Number</Text>
            <TextInput style={s.input} placeholder="e.g. HSE-2024-001" placeholderTextColor={C.light} value={upForm.reference} onChangeText={v=>setUpForm(p=>({...p,reference:v}))} />

            <Text style={s.fLabel}>Category</Text>
            <View style={s.chipRow}>
              {CATEGORIES.map(c=>(
                <TouchableOpacity key={c.id} style={[s.chip, upForm.category===c.id && { backgroundColor:c.color, borderColor:c.color }]} onPress={()=>setUpForm(p=>({...p,category:c.id}))}>
                  <MaterialCommunityIcons name={c.icon} size={12} color={upForm.category===c.id?'#fff':c.color} />
                  <Text style={[s.chipTxt, upForm.category===c.id && { color:'#fff' }]}>{c.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={s.fLabel}>File Type</Text>
            <View style={s.chipRow}>
              {FILE_TYPES.map(t=>(
                <TouchableOpacity key={t.id} style={[s.chip, upForm.type===t.id && { backgroundColor:t.color, borderColor:t.color }]} onPress={()=>setUpForm(p=>({...p,type:t.id}))}>
                  <MaterialCommunityIcons name={t.icon} size={12} color={upForm.type===t.id?'#fff':t.color} />
                  <Text style={[s.chipTxt, upForm.type===t.id && { color:'#fff' }]}>{t.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={s.fLabel}>Access Level</Text>
            <View style={s.chipRow}>
              {ACCESS_LEVELS.map(a=>(
                <TouchableOpacity key={a.id} style={[s.chip, upForm.accessLevel===a.id && { backgroundColor:a.color, borderColor:a.color }]} onPress={()=>setUpForm(p=>({...p,accessLevel:a.id}))}>
                  <MaterialCommunityIcons name={a.icon} size={12} color={upForm.accessLevel===a.id?'#fff':a.color} />
                  <Text style={[s.chipTxt, upForm.accessLevel===a.id && { color:'#fff' }]}>{a.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={s.fLabel}>Description</Text>
            <TextInput style={[s.input,{ minHeight:80, textAlignVertical:'top' }]} multiline placeholder="Brief document description…" placeholderTextColor={C.light} value={upForm.description} onChangeText={v=>setUpForm(p=>({...p,description:v}))} />

            <Text style={s.fLabel}>Tags (comma separated)</Text>
            <TextInput style={s.input} placeholder="safety, rams, site-a" placeholderTextColor={C.light} value={upForm.tags} onChangeText={v=>setUpForm(p=>({...p,tags:v}))} />

            <View style={s.fRow}>
              <View style={{ flex:1, marginRight:8 }}>
                <Text style={s.fLabel}>Expiry Date</Text>
                <TextInput style={s.input} placeholder="YYYY-MM-DD" placeholderTextColor={C.light} value={upForm.expiryDate} onChangeText={v=>setUpForm(p=>({...p,expiryDate:v}))} />
              </View>
              <View style={{ flex:1, marginLeft:8 }}>
                <Text style={s.fLabel}>Review Date</Text>
                <TextInput style={s.input} placeholder="YYYY-MM-DD" placeholderTextColor={C.light} value={upForm.reviewDate} onChangeText={v=>setUpForm(p=>({...p,reviewDate:v}))} />
              </View>
            </View>

            <TouchableOpacity style={[s.filePicker, upForm.pickedFile && { borderColor: C.primary, backgroundColor: `${C.primary}10` }]} onPress={handlePickFile}>
              <MaterialCommunityIcons name={upForm.pickedFile ? 'file-check' : 'file-upload-outline'} size={32} color={C.primary} />
              {upForm.pickedFile ? (
                <>
                  <Text style={[s.filePickerTxt, { color: C.primary }]}>{upForm.pickedFile.name}</Text>
                  <Text style={s.filePickerSub}>{upForm.pickedFile.size} · Tap to change</Text>
                </>
              ) : (
                <>
                  <Text style={s.filePickerTxt}>Tap to choose file</Text>
                  <Text style={s.filePickerSub}>PDF · DOCX · XLSX · DWG · DXF · PPT · ZIP and more</Text>
                </>
              )}
            </TouchableOpacity>

            <View style={s.fActions}>
              <TouchableOpacity style={s.cancelBtn} onPress={() => setShowUpload(false)}><Text style={s.cancelBtnTxt}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity
                style={[s.submitBtn, isUploading && { opacity:0.7 }]}
                onPress={handleUpload}
                disabled={isUploading}
              >
                {isUploading
                  ? <ActivityIndicator size="small" color="#fff" />
                  : <MaterialCommunityIcons name="cloud-upload-outline" size={16} color="#fff" />
                }
                <Text style={s.submitBtnTxt}>{isUploading ? 'Uploading…' : 'Upload Document'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )} contentContainerStyle={{ paddingBottom:60 }} />
      </SafeAreaView>
    </Modal>
  );

  // ─── FILTER MODAL ─────────────────────────────────────────────────────────
  const renderFilterModal = () => (
    <Modal visible={showFilter} transparent animationType="slide" onRequestClose={() => setShowFilter(false)}>
      <View style={s.sheet}>
        <View style={s.sheetBox}>
          <View style={s.sheetHd}>
            <View style={s.sheetHandle} />
            <View style={{ flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingBottom:12, borderBottomWidth:1, borderBottomColor:C.border }}>
              <MaterialCommunityIcons name="filter-variant" size={20} color={C.primary} />
              <Text style={[s.sheetTitle,{flex:1,marginLeft:8}]}>Filter Documents</Text>
              <TouchableOpacity onPress={() => setShowFilter(false)}><Ionicons name="close" size={22} color={C.mid} /></TouchableOpacity>
            </View>
          </View>
          <FlatList data={[1]} keyExtractor={()=>'f'} renderItem={() => (
            <View style={{ padding:16 }}>
              {[
                { label:'Category',       key:'category',       opts:[{id:'all',label:'All',color:C.mid},...CATEGORIES] },
                { label:'File Type',      key:'type',           opts:[{id:'all',label:'All',color:C.mid},...FILE_TYPES] },
                { label:'Status',         key:'status',         opts:[{id:'all',label:'All',color:C.mid},...STATUSES] },
                { label:'Access Level',   key:'accessLevel',    opts:[{id:'all',label:'All',color:C.mid},...ACCESS_LEVELS] },
                { label:'Approval Status',key:'approvalStatus', opts:[{id:'all',label:'All',color:C.mid},{id:'approved',label:'Approved',color:C.success},{id:'pending',label:'Pending',color:C.warning},{id:'rejected',label:'Rejected',color:C.danger}] },
              ].map(section => (
                <View key={section.key}>
                  <Text style={s.filterSecTitle}>{section.label}</Text>
                  <View style={s.chipRow}>
                    {section.opts.map(o => (
                      <TouchableOpacity key={o.id}
                        style={[s.chip, filters[section.key]===o.id && { backgroundColor:o.color||C.primary, borderColor:o.color||C.primary }]}
                        onPress={() => setFilters(p=>({...p,[section.key]:o.id}))}>
                        <Text style={[s.chipTxt, filters[section.key]===o.id && { color:'#fff' }]}>{o.label}</Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              ))}
            </View>
          )} />
          <View style={s.sheetActions}>
            <TouchableOpacity style={s.cancelBtn} onPress={() => setFilters({ category:'all', type:'all', status:'all', accessLevel:'all', approvalStatus:'all' })}><Text style={s.cancelBtnTxt}>Reset All</Text></TouchableOpacity>
            <TouchableOpacity style={[s.submitBtn,{flex:1}]} onPress={() => setShowFilter(false)}><Text style={s.submitBtnTxt}>Apply Filters</Text></TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ─── SORT MODAL ───────────────────────────────────────────────────────────
  const renderSortModal = () => (
    <Modal visible={showSort} transparent animationType="slide" onRequestClose={() => setShowSort(false)}>
      <View style={s.sheet}>
        <View style={[s.sheetBox, { maxHeight:height*0.6 }]}>
          <View style={s.sheetHd}>
            <View style={s.sheetHandle} />
            <View style={{ flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingBottom:12, borderBottomWidth:1, borderBottomColor:C.border }}>
              <MaterialCommunityIcons name="sort-variant" size={20} color={C.primary} />
              <Text style={[s.sheetTitle,{flex:1,marginLeft:8}]}>Sort Documents</Text>
              <TouchableOpacity onPress={() => setShowSort(false)}><Ionicons name="close" size={22} color={C.mid} /></TouchableOpacity>
            </View>
          </View>
          <FlatList
            data={SORT_OPTIONS} keyExtractor={o=>o.id}
            renderItem={({ item:o }) => (
              <TouchableOpacity style={[s.sortRow, sortBy===o.id && s.sortRowActive]} onPress={() => { setSort(o.id); setShowSort(false); }}>
                <Text style={[s.sortRowTxt, sortBy===o.id && { color:C.primary, fontWeight:'700' }]}>{o.label}</Text>
                {sortBy===o.id && <Ionicons name="checkmark" size={18} color={C.primary} />}
              </TouchableOpacity>
            )}
            contentContainerStyle={{ padding:8 }}
          />
        </View>
      </View>
    </Modal>
  );

  // ─── DETAIL MODAL ─────────────────────────────────────────────────────────
  const renderDetailModal = () => {
    if (!selDoc) return null;
    const cat    = getCat(selDoc.category);
    const type   = getType(selDoc.type);
    const status = getStatus(selDoc.status);
    const access = getAccess(selDoc.accessLevel);
    const days   = daysUntil(selDoc.expiryDate);

    return (
      <Modal visible={showDetail} animationType="slide" onRequestClose={() => setShowDetail(false)}>
        <SafeAreaView style={s.modalFull}>
          <LinearGradient colors={[C.primary,'#1565c0']} style={s.modalHd}>
            <TouchableOpacity onPress={() => setShowDetail(false)}><Ionicons name="arrow-back" size={22} color="#fff" /></TouchableOpacity>
            <Text style={s.modalTitle} numberOfLines={1}>{selDoc.name}</Text>
            <TouchableOpacity onPress={() => { setEditForm({ ...selDoc, tags:selDoc.tags?.join(', ')||'' }); setShowEdit(true); }}>
              <MaterialCommunityIcons name="pencil" size={22} color="#fff" />
            </TouchableOpacity>
          </LinearGradient>

          <FlatList data={[1]} keyExtractor={()=>'det'} renderItem={() => (
            <View style={{ padding:14 }}>

              {/* ── Hero ── */}
              <View style={s.detHero}>
                <View style={[s.detHeroIcon, { backgroundColor:`${type.color}18` }]}>
                  <MaterialCommunityIcons name={type.icon} size={44} color={type.color} />
                </View>
                <Text style={s.detHeroName}>{selDoc.name}</Text>
                {selDoc.reference && <Text style={s.detHeroRef}>{selDoc.reference}</Text>}
                <Text style={s.detHeroCat}>{cat.label} · {type.label} · v{selDoc.version}</Text>
                {/* expiry banner */}
                {days !== null && (
                  <View style={[s.expiryBanner, { backgroundColor: days<=0 ? '#fef2f2' : days<=30 ? '#fff7ed' : '#f0fdf4' }]}>
                    <MaterialCommunityIcons name="calendar-clock" size={13} color={days<=0?C.danger:days<=30?C.warning:C.success} />
                    <Text style={[s.expiryBannerTxt, { color:days<=0?C.danger:days<=30?C.warning:C.success }]}>
                      <Text>{days<=0 ? `EXPIRED ${fmtDate(selDoc.expiryDate)}` : `Expires in ${days} days · ${fmtDate(selDoc.expiryDate)}`}</Text>
                    </Text>
                  </View>
                )}
              </View>

              {/* ── Quick actions ── */}
              <View style={s.quickRow}>
                {[
                  { icon:'download',           label:'Download',  color:C.primary,  onPress:() => handleDownload(selDoc) },
                  { icon:'share-variant',       label:'Share',     color:C.accent,   onPress:() => handleShare(selDoc) },
                  { icon:'printer-outline',     label:'Print',     color:'#475569',  onPress:() => handlePrint(selDoc) },
                  { icon:'file-pdf-box',        label:'Export PDF',color:'#dc2626',  onPress:() => handleExportPDF(selDoc) },
                  { icon:'eye-outline',         label:'View File', color:C.success,  onPress:() => handleViewFile(selDoc) },
                  { icon:'magnify-scan',        label:'Review',    color:'#f59e0b',  onPress:() => { setReviewNote(''); setShowReview(true); } },
                  { icon:'content-copy',        label:'Duplicate', color:C.purple,   onPress:() => handleDuplicate(selDoc) },
                  { icon:'history',             label:'Versions',  color:C.gold,     onPress:() => setShowVersions(true) },
                  { icon: selDoc.favourite ? 'heart' : 'heart-outline', label:'Favourite', color:C.danger, onPress:()=>handleToggleFavourite(selDoc) },
                  { icon: selDoc.pinned   ? 'pin'   : 'pin-outline',   label:selDoc.pinned?'Unpin':'Pin',  color:C.gold,  onPress:()=>handleTogglePin(selDoc) },
                  { icon: selDoc.locked   ? 'lock-open-variant-outline' : 'lock-outline', label:selDoc.locked?'Unlock':'Lock', color:C.mid, onPress:()=>handleToggleLock(selDoc) },
                  { icon:'bell-plus-outline',   label:'Reminder',  color:C.accent,   onPress:()=>setShowReminders(true) },
                  { icon:'file-eye-outline',    label:'Preview',   color:C.accent,   onPress:()=>setShowPreview(true) },
                  { icon:'history',             label:'Audit Log', color:C.purple,   onPress:()=>setShowAudit(true) },
                ].map(a => (
                  <TouchableOpacity key={a.label} style={s.quickBtn} onPress={a.onPress}>
                    <View style={[s.quickBtnIcon, { backgroundColor:`${a.color}18` }]}>
                      <MaterialCommunityIcons name={a.icon} size={20} color={a.color} />
                    </View>
                    <Text style={s.quickBtnLbl}>{a.label}</Text>
                  </TouchableOpacity>
                ))}
              </View>

              {/* ── Document Info ── */}
              <View style={s.sec}>
                <Text style={s.secTitle}>Document Information</Text>
                {[
                  ['File Type',      type.label],
                  ['File Format',    (selDoc.type||'').toUpperCase()],
                  ['File Size',      selDoc.size],
                  ['Version',        `v${selDoc.version}`],
                  ['Author',         selDoc.author],
                  ['Department',     selDoc.department],
                  ['Reference',      selDoc.reference],
                  ['Location',       selDoc.location],
                  ['Checksum',       selDoc.checksum],
                ].filter(([,v])=>v).map(([l,v]) => (
                  <View key={l} style={s.dRow}>
                    <Text style={s.dLabel}>{l}</Text>
                    <Text style={s.dVal} numberOfLines={1}>{v}</Text>
                  </View>
                ))}
                <View style={s.dRow}><Text style={s.dLabel}>Status</Text><View style={[s.statusPill,{backgroundColor:`${status.color}18`}]}><Text style={[s.statusPillTxt,{color:status.color}]}>{status.label}</Text></View></View>
                <View style={s.dRow}><Text style={s.dLabel}>Access Level</Text>
                  <View style={{ flexDirection:'row', alignItems:'center', gap:4 }}>
                    <MaterialCommunityIcons name={access.icon} size={13} color={access.color} />
                    <Text style={[s.dVal,{color:access.color}]}>{access.label}</Text>
                  </View>
                </View>
              </View>

              {/* ── Approval ── */}
              <View style={s.sec}>
                <Text style={s.secTitle}>Approval</Text>
                <View style={s.dRow}><Text style={s.dLabel}>Status</Text>
                  <View style={[s.statusPill,{
                    backgroundColor: selDoc.approvalStatus==='approved'?'#d1fae5':selDoc.approvalStatus==='rejected'?'#fee2e2':'#fef3c7'
                  }]}>
                    <Text style={[s.statusPillTxt,{
                      color: selDoc.approvalStatus==='approved'?C.success:selDoc.approvalStatus==='rejected'?C.danger:C.warning
                    }]}>
                      {(selDoc.approvalStatus||'pending').toUpperCase()}
                    </Text>
                  </View>
                </View>
                {selDoc.approvedBy    && <View style={s.dRow}><Text style={s.dLabel}>Approved By</Text><Text style={s.dVal}>{selDoc.approvedBy}</Text></View>}
                {selDoc.approvalDate  && <View style={s.dRow}><Text style={s.dLabel}>Approval Date</Text><Text style={s.dVal}>{fmtDate(selDoc.approvalDate)}</Text></View>}
                {selDoc.rejectionReason && (
                  <View style={[s.sec,{margin:0,backgroundColor:'#fef2f2',marginTop:8}]}>
                    <Text style={[s.secTitle,{color:C.danger}]}>Rejection Reason</Text>
                    <Text style={[s.secBody,{color:C.danger}]}>{selDoc.rejectionReason}</Text>
                  </View>
                )}
                {selDoc.approvalStatus === 'pending' && (
                  <View style={{ flexDirection:'row', gap:10, marginTop:12 }}>
                    <TouchableOpacity style={s.approveBtn} onPress={() => handleApprove(selDoc)}>
                      <MaterialCommunityIcons name="check-circle" size={17} color="#fff" />
                      <Text style={s.approveBtnTxt}>Approve</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={s.rejectBtn} onPress={() => setShowReject(true)}>
                      <MaterialCommunityIcons name="close-circle" size={17} color="#fff" />
                      <Text style={s.rejectBtnTxt}>Reject</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              {/* ── Dates ── */}
              <View style={s.sec}>
                <Text style={s.secTitle}>Dates</Text>
                {[['Created',fmtDate(selDoc.createdDate)],['Modified',fmtDate(selDoc.modifiedDate)],
                  selDoc.expiryDate && ['Expiry',fmtDate(selDoc.expiryDate)],
                  selDoc.reviewDate && ['Review',fmtDate(selDoc.reviewDate)],
                ].filter(Boolean).map(([l,v]) => (
                  <View key={l} style={s.dRow}><Text style={s.dLabel}>{l}</Text><Text style={s.dVal}>{v}</Text></View>
                ))}
              </View>

              {/* ── Description ── */}
              {!!selDoc.description && (
                <View style={s.sec}>
                  <Text style={s.secTitle}>Description</Text>
                  <Text style={s.secBody}>{selDoc.description}</Text>
                </View>
              )}

              {/* ── Tags ── */}
              {selDoc.tags?.length > 0 && (
                <View style={s.sec}>
                  <Text style={s.secTitle}>Tags</Text>
                  <View style={s.chipRow}>
                    {selDoc.tags.map((t,i) => <View key={i} style={s.tagChip}><Text style={s.tagChipTxt}>#{t}</Text></View>)}
                  </View>
                </View>
              )}

              {/* ── Statistics ── */}
              <View style={s.sec}>
                <Text style={s.secTitle}>Statistics</Text>
                <View style={{ flexDirection:'row', gap:10 }}>
                  {[
                    { icon:'eye-outline',       label:'Views',     v:selDoc.viewCount },
                    { icon:'download-outline',  label:'Downloads', v:selDoc.downloadCount },
                    { icon:'comment-outline',   label:'Comments',  v:selDoc.comments?.length||0 },
                    { icon:'history',           label:'Versions',  v:selDoc.versions?.length||1 },
                  ].map(st => (
                    <View key={st.label} style={s.statCard}>
                      <MaterialCommunityIcons name={st.icon} size={20} color={C.primary} />
                      <Text style={s.statCardVal}>{st.v}</Text>
                      <Text style={s.statCardLbl}>{st.label}</Text>
                    </View>
                  ))}
                </View>
              </View>

              {/* ── Version history preview ── */}
              <View style={s.sec}>
                <View style={s.secHdRow}>
                  <Text style={s.secTitle}>Version History</Text>
                  <View style={{ flexDirection:'row', gap:8 }}>
                    <TouchableOpacity style={s.miniBtn} onPress={() => setShowAddVer(true)}>
                      <Ionicons name="add" size={14} color={C.primary} /><Text style={s.miniBtnTxt}>Add Version</Text>
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => setShowVersions(true)}><Text style={s.viewAll}>View All</Text></TouchableOpacity>
                  </View>
                </View>
                {selDoc.versions?.slice(0,3).map((v,i) => (
                  <View key={i} style={s.verRow}>
                    <View style={s.verBadge}><Text style={s.verBadgeTxt}>v{v.version}</Text></View>
                    <View style={{ flex:1, marginLeft:10 }}>
                      <Text style={s.verChanges}>{v.changes}</Text>
                      <Text style={s.verMeta}>by {v.author} · {fmtDate(v.date)}</Text>
                    </View>
                    {i===0 && <View style={s.latestBadge}><Text style={s.latestBadgeTxt}>Latest</Text></View>}
                  </View>
                ))}
              </View>

              {/* ── Comments ── */}
              <View style={s.sec}>
                <View style={s.secHdRow}>
                  <Text style={s.secTitle}>Comments ({selDoc.comments?.length||0})</Text>
                  <TouchableOpacity style={s.addBtn} onPress={() => setShowComment(true)}>
                    <Ionicons name="add" size={17} color={C.primary} />
                  </TouchableOpacity>
                </View>
                {selDoc.comments?.length > 0 ? selDoc.comments.map(cmt => (
                  <View key={cmt.id} style={[s.cmtItem, cmt.resolved && { opacity:0.5 }]}>
                    <View style={s.cmtAvatar}><Text style={s.cmtAvatarTxt}>{cmt.avatar||cmt.author[0]}</Text></View>
                    <View style={{ flex:1, marginLeft:10 }}>
                      <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:2 }}>
                        <Text style={s.cmtAuthor}>{cmt.author}</Text>
                        <Text style={s.cmtDate}>{fmtDate(cmt.date)}</Text>
                      </View>
                      <Text style={s.cmtText}>{cmt.text}</Text>
                      <View style={{ flexDirection:'row', gap:12, marginTop:4 }}>
                        <TouchableOpacity onPress={() => handleResolveComment(cmt)}>
                          <Text style={[s.cmtResolve, cmt.resolved && { color:C.success }]}>
                            <Text>{cmt.resolved ? '✓ Resolved' : 'Mark resolved'}</Text>
                          </Text>
                        </TouchableOpacity>
                        <TouchableOpacity onPress={() => handleDeleteComment(cmt)}>
                          <Text style={[s.cmtResolve, { color:C.danger }]}>Delete</Text>
                        </TouchableOpacity>
                      </View>
                    </View>
                  </View>
                )) : <Text style={s.emptyMini}>No comments yet. Tap + to add one.</Text>}
              </View>

              {/* ── Reminders ── */}
              <View style={s.sec}>
                <View style={s.secHdRow}>
                  <Text style={s.secTitle}>Reminders ({selDoc.reminders?.length||0})</Text>
                  <TouchableOpacity style={s.addBtn} onPress={() => setShowReminders(true)}>
                    <Ionicons name="add" size={17} color={C.primary} />
                  </TouchableOpacity>
                </View>
                {selDoc.reminders?.length > 0 ? selDoc.reminders.map(r => (
                  <View key={r.id} style={s.remRow}>
                    <MaterialCommunityIcons name="bell-outline" size={16} color={C.warning} />
                    <View style={{ flex:1, marginLeft:8 }}>
                      <Text style={s.remDate}>{fmtDate(r.date)}</Text>
                      <Text style={s.remNote}>{r.note}</Text>
                    </View>
                    <TouchableOpacity onPress={() => handleDeleteReminder(r.id)}>
                      <Ionicons name="close" size={18} color={C.light} />
                    </TouchableOpacity>
                  </View>
                )) : <Text style={s.emptyMini}>No reminders set.</Text>}
              </View>

              {/* ── Share trail ── */}
              {selDoc.shareTrail?.length > 0 && (
                <View style={s.sec}>
                  <View style={s.secHdRow}>
                    <Text style={s.secTitle}>Share History</Text>
                    <TouchableOpacity onPress={() => setShowShareTrail(true)}><Text style={s.viewAll}>View All</Text></TouchableOpacity>
                  </View>
                  {selDoc.shareTrail.slice(0,2).map((st,i) => (
                    <View key={i} style={s.stRow}>
                      <MaterialCommunityIcons name="share-variant-outline" size={15} color={C.accent} />
                      <Text style={s.stTxt}>{st.sharedWith} · {fmtDate(st.date)}</Text>
                    </View>
                  ))}
                </View>
              )}

              {/* ── Tags management ── */}
              <View style={s.sec}>
                <Text style={s.secTitle}>Tags</Text>
                <View style={s.tagsRow}>
                  {(selDoc.tags||[]).map((t,i) => (
                    <TouchableOpacity key={i} style={[s.tagChip,{ flexDirection:'row', alignItems:'center', gap:4, paddingRight:6 }]}
                      onPress={() => handleRemoveTag(t)}>
                      <Text style={s.tagChipTxt}>#{t}</Text>
                      <Ionicons name="close" size={10} color={C.mid} />
                    </TouchableOpacity>
                  ))}
                </View>
                <View style={{ flexDirection:'row', gap:8, marginTop:8 }}>
                  <TextInput
                    style={[s.input,{ flex:1, marginBottom:0, paddingVertical:8 }]}
                    placeholder="Add tag…" placeholderTextColor={C.light}
                    value={newTag} onChangeText={setNewTag}
                    onSubmitEditing={() => { handleAddTag(newTag); setNewTag(''); }}
                    returnKeyType="done"
                  />
                  <TouchableOpacity style={[s.submitBtn,{ flex:0, paddingHorizontal:16 }]}
                    onPress={() => { handleAddTag(newTag); setNewTag(''); }}>
                    <Text style={s.submitBtnTxt}>Add</Text>
                  </TouchableOpacity>
                </View>
              </View>

              {/* ── Danger zone ── */}
              <View style={s.sec}>
                <Text style={s.secTitle}>Actions</Text>
                <View style={{ flexDirection:'row', flexWrap:'wrap', gap:10 }}>
                  {selDoc.status !== 'archived' ? (
                    <TouchableOpacity style={s.dangerBtn} onPress={() => handleArchive(selDoc)}>
                      <MaterialCommunityIcons name="archive" size={16} color={C.warning} />
                      <Text style={[s.dangerBtnTxt,{color:C.warning}]}>Archive</Text>
                    </TouchableOpacity>
                  ) : (
                    <TouchableOpacity style={s.dangerBtn} onPress={() => handleRestore(selDoc)}>
                      <MaterialCommunityIcons name="restore" size={16} color={C.success} />
                      <Text style={[s.dangerBtnTxt,{color:C.success}]}>Restore</Text>
                    </TouchableOpacity>
                  )}
                  <TouchableOpacity style={s.dangerBtn} onPress={() => handleDelete(selDoc)}>
                    <MaterialCommunityIcons name="delete-outline" size={16} color={C.danger} />
                    <Text style={[s.dangerBtnTxt,{color:C.danger}]}>Delete</Text>
                  </TouchableOpacity>
                </View>
              </View>
            </View>
          )} contentContainerStyle={{ paddingBottom:60 }} />
        </SafeAreaView>
      </Modal>
    );
  };

  // ─── EDIT MODAL ───────────────────────────────────────────────────────────
  const renderEditModal = () => (
    <Modal visible={showEdit} animationType="slide" onRequestClose={() => setShowEdit(false)}>
      <SafeAreaView style={s.modalFull}>
        <LinearGradient colors={[C.primary,'#1565c0']} style={s.modalHd}>
          <TouchableOpacity onPress={() => setShowEdit(false)}><Ionicons name="close" size={24} color="#fff" /></TouchableOpacity>
          <Text style={s.modalTitle}>Edit Document</Text>
          <TouchableOpacity onPress={handleSaveEdit}><Text style={s.modalSave}>Save</Text></TouchableOpacity>
        </LinearGradient>
        <FlatList data={[1]} keyExtractor={()=>'ed'} renderItem={() => (
          <View style={s.formBody}>
            <Text style={s.fLabel}>Document Name</Text>
            <TextInput style={s.input} value={editForm.name||''} onChangeText={v=>setEditForm(p=>({...p,name:v}))} />

            <View style={s.fRow}>
              <View style={{ flex:1, marginRight:8 }}>
                <Text style={s.fLabel}>Author</Text>
                <TextInput style={s.input} value={editForm.author||''} onChangeText={v=>setEditForm(p=>({...p,author:v}))} />
              </View>
              <View style={{ flex:1, marginLeft:8 }}>
                <Text style={s.fLabel}>Department</Text>
                <TextInput style={s.input} value={editForm.department||''} onChangeText={v=>setEditForm(p=>({...p,department:v}))} />
              </View>
            </View>

            <Text style={s.fLabel}>Reference</Text>
            <TextInput style={s.input} value={editForm.reference||''} onChangeText={v=>setEditForm(p=>({...p,reference:v}))} />

            <Text style={s.fLabel}>Category</Text>
            <View style={s.chipRow}>
              {CATEGORIES.map(c=>(
                <TouchableOpacity key={c.id} style={[s.chip, editForm.category===c.id && { backgroundColor:c.color, borderColor:c.color }]} onPress={()=>setEditForm(p=>({...p,category:c.id}))}>
                  <Text style={[s.chipTxt, editForm.category===c.id && { color:'#fff' }]}>{c.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={s.fLabel}>Access Level</Text>
            <View style={s.chipRow}>
              {ACCESS_LEVELS.map(a=>(
                <TouchableOpacity key={a.id} style={[s.chip, editForm.accessLevel===a.id && { backgroundColor:a.color, borderColor:a.color }]} onPress={()=>setEditForm(p=>({...p,accessLevel:a.id}))}>
                  <Text style={[s.chipTxt, editForm.accessLevel===a.id && { color:'#fff' }]}>{a.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={s.fLabel}>Status</Text>
            <View style={s.chipRow}>
              {STATUSES.map(st=>(
                <TouchableOpacity key={st.id} style={[s.chip, editForm.status===st.id && { backgroundColor:st.color, borderColor:st.color }]} onPress={()=>setEditForm(p=>({...p,status:st.id}))}>
                  <Text style={[s.chipTxt, editForm.status===st.id && { color:'#fff' }]}>{st.label}</Text>
                </TouchableOpacity>
              ))}
            </View>

            <Text style={s.fLabel}>Description</Text>
            <TextInput style={[s.input,{ minHeight:80, textAlignVertical:'top' }]} multiline value={editForm.description||''} onChangeText={v=>setEditForm(p=>({...p,description:v}))} />

            <Text style={s.fLabel}>Tags (comma separated)</Text>
            <TextInput style={s.input} value={typeof editForm.tags==='string' ? editForm.tags : editForm.tags?.join(', ')||''} onChangeText={v=>setEditForm(p=>({...p,tags:v}))} />

            <View style={s.fRow}>
              <View style={{ flex:1, marginRight:8 }}>
                <Text style={s.fLabel}>Expiry Date</Text>
                <TextInput style={s.input} placeholder="YYYY-MM-DD" placeholderTextColor={C.light} value={editForm.expiryDate||''} onChangeText={v=>setEditForm(p=>({...p,expiryDate:v}))} />
              </View>
              <View style={{ flex:1, marginLeft:8 }}>
                <Text style={s.fLabel}>Review Date</Text>
                <TextInput style={s.input} placeholder="YYYY-MM-DD" placeholderTextColor={C.light} value={editForm.reviewDate||''} onChangeText={v=>setEditForm(p=>({...p,reviewDate:v}))} />
              </View>
            </View>

            <View style={s.fActions}>
              <TouchableOpacity style={s.cancelBtn} onPress={() => setShowEdit(false)}><Text style={s.cancelBtnTxt}>Cancel</Text></TouchableOpacity>
              <TouchableOpacity style={s.submitBtn} onPress={handleSaveEdit}><Text style={s.submitBtnTxt}>Save Changes</Text></TouchableOpacity>
            </View>
          </View>
        )} contentContainerStyle={{ paddingBottom:60 }} />
      </SafeAreaView>
    </Modal>
  );

  // ─── VERSIONS MODAL ───────────────────────────────────────────────────────
  const renderVersionsModal = () => (
    <Modal visible={showVersions} transparent animationType="slide" onRequestClose={() => setShowVersions(false)}>
      <View style={s.sheet}>
        <View style={[s.sheetBox, { maxHeight:height*0.75 }]}>
          <View style={s.sheetHd}>
            <View style={s.sheetHandle} />
            <View style={{ flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingBottom:12, borderBottomWidth:1, borderBottomColor:C.border }}>
              <MaterialCommunityIcons name="history" size={20} color={C.primary} />
              <Text style={[s.sheetTitle,{flex:1,marginLeft:8}]}>Version History</Text>
              <TouchableOpacity style={s.miniBtn} onPress={() => { setShowVersions(false); setShowAddVer(true); }}>
                <Ionicons name="add" size={14} color={C.primary} /><Text style={s.miniBtnTxt}>Add</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{ marginLeft:8 }} onPress={() => setShowVersions(false)}><Ionicons name="close" size={22} color={C.mid} /></TouchableOpacity>
            </View>
          </View>
          <FlatList
            data={selDoc?.versions||[]} keyExtractor={(_,i)=>String(i)}
            renderItem={({ item:v, index:i }) => (
              <View style={s.verItemFull}>
                <View style={s.verBadge}><Text style={s.verBadgeTxt}>v{v.version}</Text></View>
                <View style={{ flex:1, marginLeft:12 }}>
                  <Text style={s.verChanges}>{v.changes}</Text>
                  <Text style={s.verMeta}>by {v.author} · {fmtDate(v.date)}</Text>
                  {v.sizeBytes > 0 && <Text style={s.verMeta}>{(v.sizeBytes/1e6).toFixed(1)} MB</Text>}
                </View>
                {i===0 && <View style={s.latestBadge}><Text style={s.latestBadgeTxt}>Latest</Text></View>}
              </View>
            )}
            ItemSeparatorComponent={() => <View style={{ height:1, backgroundColor:C.border, marginHorizontal:16 }} />}
            contentContainerStyle={{ padding:16, paddingBottom:40 }}
          />
        </View>
      </View>
    </Modal>
  );

  // ─── SMALL SHEET MODALS ───────────────────────────────────────────────────
  const renderSmallSheet = ({ visible, onClose, icon, iconColor, title, children }) => (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <View style={s.sheet}>
        <View style={s.sheetBox}>
          <View style={s.sheetHandle} />
          <View style={{ flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingVertical:22, borderBottomWidth:1, borderBottomColor:C.border }}>
            <MaterialCommunityIcons name={icon} size={20} color={iconColor||C.primary} />
            <Text style={[s.sheetTitle,{flex:1,marginLeft:8}]}>{title}</Text>
            <TouchableOpacity onPress={onClose}><Ionicons name="close" size={22} color={C.mid} /></TouchableOpacity>
          </View>
          <View style={{ padding:16 }}>{children}</View>
        </View>
      </View>
    </Modal>
  );

  // ─── ANALYTICS MODAL ──────────────────────────────────────────────────────
  // Analytics modal – loads live data from API on open
  const [apiAnalytics, setApiAnalytics] = React.useState(null);
  React.useEffect(() => {
    if (!showAnalytics) return;
    Promise.allSettled([
      api.get(EP.analytics),
      api.get(EP.analyticsByCat),
      api.get(EP.analyticsByType),
      api.get(EP.mostViewed),
      api.get(EP.mostDownloaded),
      api.get(EP.expiringDocs),
    ]).then(results => {
      const dashboard  = results[0].status==='fulfilled' ? results[0].value.data?.data : null;
      const byCat      = results[1].status==='fulfilled' ? results[1].value.data?.data : null;
      const byType     = results[2].status==='fulfilled' ? results[2].value.data?.data : null;
      const topViewed  = results[3].status==='fulfilled' ? results[3].value.data?.data : null;
      const topDL      = results[4].status==='fulfilled' ? results[4].value.data?.data : null;
      const expiring   = results[5].status==='fulfilled' ? results[5].value.data?.data : null;
      if (dashboard || byCat || byType) {
        setApiAnalytics({ dashboard, byCat, byType, topViewed, topDL, expiring });
      }
    }).catch(() => {});
  }, [showAnalytics]);

  const renderAnalyticsModal = () => (
    <Modal visible={showAnalytics} animationType="slide" onRequestClose={() => setShowAnalytics(false)}>
      <SafeAreaView style={s.modalFull}>
        <LinearGradient colors={[C.primary,'#1565c0']} style={s.modalHd}>
          <TouchableOpacity onPress={() => setShowAnalytics(false)}><Ionicons name="close" size={24} color="#fff" /></TouchableOpacity>
          <Text style={s.modalTitle}>Analytics &amp; Insights</Text>
          <View style={{ width:24 }} />
        </LinearGradient>
        <FlatList data={[1]} keyExtractor={()=>'an'} renderItem={() => (
          <View style={{ padding:14 }}>

            {/* KPI Grid */}
            <View style={s.kpiGrid}>
              {[
                { label:'Total Docs',   v:analytics.total,      icon:'file-multiple',        c:C.primary },
                { label:'Active',       v:analytics.active,     icon:'check-circle-outline', c:C.success },
                { label:'Drafts',       v:analytics.draft,      icon:'pencil-outline',       c:C.mid },
                { label:'Pending',      v:analytics.pending,    icon:'clock-alert-outline',  c:C.warning },
                { label:'Expiring',     v:analytics.expiring,   icon:'calendar-alert',       c:C.gold },
                { label:'Expired',      v:analytics.expired,    icon:'calendar-remove',      c:C.danger },
                { label:'Archived',     v:analytics.archived,   icon:'archive-outline',      c:C.light },
                { label:'Pinned',       v:analytics.pinned,     icon:'pin-outline',          c:C.gold },
                { label:'Favourites',   v:analytics.favourites, icon:'heart-outline',        c:C.danger },
                { label:'Locked',       v:analytics.locked,     icon:'lock-outline',         c:C.mid },
                { label:'Total Views',  v:analytics.totalViews, icon:'eye-outline',          c:C.accent },
                { label:'Downloads',    v:analytics.totalDL,    icon:'download-outline',     c:C.purple },
              ].map(k => (
                <View key={k.label} style={s.kpiCard}>
                  <MaterialCommunityIcons name={k.icon} size={24} color={k.c} />
                  <Text style={[s.kpiVal,{color:k.c}]}>{k.v}</Text>
                  <Text style={s.kpiLbl}>{k.label}</Text>
                </View>
              ))}
            </View>

            <View style={[s.sec,{marginTop:8}]}>
              <Text style={s.secTitle}>Total Storage Used</Text>
              <Text style={[s.kpiVal,{color:C.primary,fontSize:28,marginTop:4}]}>{analytics.totalSize}</Text>
            </View>

            {/* By Category */}
            <View style={s.sec}>
              <Text style={s.secTitle}>By Category</Text>
              {analytics.byCat.filter(c=>c.count>0).map(c => {
                const pct = analytics.total ? (c.count/analytics.total)*100 : 0;
                return (
                  <View key={c.id} style={s.barRow}>
                    <View style={s.barLbl}>
                      <MaterialCommunityIcons name={c.icon} size={12} color={c.color} />
                      <Text style={s.barLblTxt}>{c.label}</Text>
                    </View>
                    <View style={s.barTrack}><View style={[s.barFill,{width:`${Math.max(pct,2)}%`,backgroundColor:c.color}]} /></View>
                    <Text style={s.barCount}>{c.count}</Text>
                  </View>
                );
              })}
            </View>

            {/* By File Type */}
            <View style={s.sec}>
              <Text style={s.secTitle}>By File Type</Text>
              {analytics.byType.filter(t=>t.count>0).map(t => {
                const pct = analytics.total ? (t.count/analytics.total)*100 : 0;
                return (
                  <View key={t.id} style={s.barRow}>
                    <View style={s.barLbl}>
                      <MaterialCommunityIcons name={t.icon} size={12} color={t.color} />
                      <Text style={s.barLblTxt}>{t.label}</Text>
                    </View>
                    <View style={s.barTrack}><View style={[s.barFill,{width:`${Math.max(pct,2)}%`,backgroundColor:t.color}]} /></View>
                    <Text style={s.barCount}>{t.count}</Text>
                  </View>
                );
              })}
            </View>

            {/* By Access Level */}
            <View style={s.sec}>
              <Text style={s.secTitle}>By Access Level</Text>
              {analytics.byAccess.map(a => {
                const pct = analytics.total ? (a.count/analytics.total)*100 : 0;
                return (
                  <View key={a.id} style={s.barRow}>
                    <View style={s.barLbl}>
                      <MaterialCommunityIcons name={a.icon} size={12} color={a.color} />
                      <Text style={s.barLblTxt}>{a.label}</Text>
                    </View>
                    <View style={s.barTrack}><View style={[s.barFill,{width:`${Math.max(pct,2)}%`,backgroundColor:a.color}]} /></View>
                    <Text style={s.barCount}>{a.count}</Text>
                  </View>
                );
              })}
            </View>

            {/* Top 5 most viewed */}
            <View style={s.sec}>
              <Text style={s.secTitle}>Most Viewed</Text>
              {[...documents].sort((a,b)=>b.viewCount-a.viewCount).slice(0,5).map((d,i) => (
                <View key={d.id} style={s.rankRow}>
                  <Text style={s.rankNum}>#{i+1}</Text>
                  <View style={{ flex:1, marginLeft:8 }}>
                    <Text style={s.rankName} numberOfLines={1}>{d.name}</Text>
                    <Text style={s.rankMeta}>{d.viewCount} views · {d.downloadCount} downloads</Text>
                  </View>
                  <MaterialCommunityIcons name={getCat(d.category).icon} size={14} color={getCat(d.category).color} />
                </View>
              ))}
            </View>

            {/* Most downloaded */}
            <View style={s.sec}>
              <Text style={s.secTitle}>Most Downloaded</Text>
              {[...documents].sort((a,b)=>b.downloadCount-a.downloadCount).slice(0,5).map((d,i) => (
                <View key={d.id} style={s.rankRow}>
                  <Text style={s.rankNum}>#{i+1}</Text>
                  <View style={{ flex:1, marginLeft:8 }}>
                    <Text style={s.rankName} numberOfLines={1}>{d.name}</Text>
                    <Text style={s.rankMeta}>{d.downloadCount} downloads · {d.viewCount} views</Text>
                  </View>
                  <MaterialCommunityIcons name={getType(d.type).icon} size={14} color={getType(d.type).color} />
                </View>
              ))}
            </View>
          </View>
        )} contentContainerStyle={{ paddingBottom:60 }} />
      </SafeAreaView>
    </Modal>
  );

  // ─── SHARE TRAIL MODAL ────────────────────────────────────────────────────
  // Share trail – refresh from API when modal opens
  const [apiShares, setApiShares] = React.useState(null);
  React.useEffect(() => {
    if (!showShareTrail || !selDoc) return;
    api.get(EP.shares(selDoc.id)).then(r => {
      if (r.data?.success && Array.isArray(r.data.data)) setApiShares(r.data.data);
    }).catch(() => {});
  }, [showShareTrail, selDoc?.id]);

  const renderShareTrailModal = () => (
    <Modal visible={showShareTrail} transparent animationType="slide" onRequestClose={() => setShowShareTrail(false)}>
      <View style={s.sheet}>
        <View style={[s.sheetBox,{maxHeight:height*0.6}]}>
          <View style={s.sheetHd}>
            <View style={s.sheetHandle} />
            <View style={{ flexDirection:'row', alignItems:'center', paddingHorizontal:16, paddingBottom:12, borderBottomWidth:1, borderBottomColor:C.border }}>
              <MaterialCommunityIcons name="share-variant-outline" size={20} color={C.accent} />
              <Text style={[s.sheetTitle,{flex:1,marginLeft:8}]}>Share History</Text>
              <TouchableOpacity onPress={() => setShowShareTrail(false)}><Ionicons name="close" size={22} color={C.mid} /></TouchableOpacity>
            </View>
          </View>
          <FlatList
            data={selDoc?.shareTrail||[]} keyExtractor={(_,i)=>String(i)}
            renderItem={({ item:st }) => (
              <View style={s.stRowFull}>
                <View style={[s.cmtAvatar,{backgroundColor:C.accent+'22'}]}><MaterialCommunityIcons name="share-variant" size={16} color={C.accent} /></View>
                <View style={{ flex:1, marginLeft:10 }}>
                  <Text style={s.cmtAuthor}>{st.sharedWith}</Text>
                  <Text style={s.cmtDate}>Shared by {st.by} · {fmtDate(st.date)}</Text>
                </View>
              </View>
            )}
            ListEmptyComponent={<Text style={[s.emptyMini,{padding:16}]}>No share history yet.</Text>}
            contentContainerStyle={{ padding:12, paddingBottom:40 }}
          />
        </View>
      </View>
    </Modal>
  );

  // ─── LOADING ──────────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <View style={s.loader}>
        <ActivityIndicator size="large" color={C.primary} />
        <Text style={s.loaderTxt}>Loading documents…</Text>
      </View>
    );
  }

  // ─── MAIN RENDER ──────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={s.container} edges={['top']}>
      <FlatList
        data={listData}
        keyExtractor={item => item.id}
        renderItem={renderItem}
        key={viewMode}                          // triggers remount when columns change
        numColumns={viewMode==='grid' ? 2 : 1}
        ListHeaderComponent={ListHeader}
        ListEmptyComponent={ListEmpty}
        contentContainerStyle={viewMode==='grid' ? s.gridContent : s.listContent}
        showsVerticalScrollIndicator={false}
        initialNumToRender={8}
        maxToRenderPerBatch={6}
        windowSize={7}
        removeClippedSubviews
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={async () => { setRefreshing(true); await init(); setRefreshing(false); }} colors={[C.primary]} tintColor={C.primary} />
        }
      />

      {/* Floating Action Button */}
      <TouchableOpacity style={s.fab} onPress={() => setShowUpload(true)}>
        <LinearGradient colors={[C.primary,'#1565c0']} style={s.fabGrad}>
          <Ionicons name="add" size={28} color="#fff" />
        </LinearGradient>
      </TouchableOpacity>

      {/* AI Assistant FAB */}
      <TouchableOpacity
        onPress={() => setShowAIChat(true)}
        style={{ position:'absolute', bottom:FAB_BOTTOM + FAB_SIZE + 12, right:IS_PROMAX ? 25 : 18, width:56, height:56, borderRadius:28, backgroundColor:'#6366F1', alignItems:'center', justifyContent:'center', elevation:8, shadowColor:'#6366F1', shadowOffset:{width:0,height:4}, shadowOpacity:0.3, shadowRadius:8, zIndex:999 }}>
        <MaterialCommunityIcons name="robot" size={24} color="#fff" />
      </TouchableOpacity>
      <AIChatWidget screenName="DocumentsControlScreen" visible={showAIChat} onClose={() => setShowAIChat(false)} title="Document AI" extraContext={{ documents }} />

      {/* ── Turn 1: Audit Trail + Preview modals ── */}
      <AuditTrailModal
        visible={showAudit}
        onClose={() => setShowAudit(false)}
        document={selDoc}
        companyId={companyId}
      />
      <DocumentPreviewModal
        visible={showPreview}
        onClose={() => setShowPreview(false)}
        document={selDoc}
        companyId={companyId}
      />

      {/* ── All modals – gated so they never mount when closed ── */}
      {showUpload     && renderUploadModal()}
      {showFilter     && renderFilterModal()}
      {showSort       && renderSortModal()}
      {showDetail     && renderDetailModal()}
      {showEdit       && renderEditModal()}
      {showVersions   && renderVersionsModal()}
      {showAnalytics  && renderAnalyticsModal()}
      {showShareTrail && renderShareTrailModal()}

      {showReview && renderSmallSheet({
        visible:showReview, onClose:()=>setShowReview(false),
        icon:'magnify-scan', iconColor:'#f59e0b', title:'Request Review',
        children: (
          <>
            <Text style={s.fLabel}>Document</Text>
            <Text style={[s.dVal,{marginBottom:12,textAlign:'left',color:C.dark}]}>{selDoc?.name}</Text>
            <Text style={s.fLabel}>Review Notes (optional)</Text>
            <TextInput style={[s.input,{minHeight:100,textAlignVertical:'top',marginBottom:16}]} multiline autoFocus
              placeholder="Describe what needs to be reviewed, any concerns or specific sections to check…"
              placeholderTextColor={C.light} value={reviewNote} onChangeText={setReviewNote} />
            <TouchableOpacity style={[s.submitBtn,{backgroundColor:'#f59e0b'}]} onPress={handleReviewSubmit}>
              <MaterialCommunityIcons name="send-check-outline" size={16} color="#fff" />
              <Text style={s.submitBtnTxt}>Submit for Review</Text>
            </TouchableOpacity>
          </>
        ),
      })}

      {showAddVer && renderSmallSheet({
        visible:showAddVer, onClose:()=>setShowAddVer(false),
        icon:'plus-circle-outline', iconColor:C.primary, title:'Add New Version',
        children: (
          <>
            <Text style={s.fLabel}>What changed in this version?</Text>
            <TextInput style={[s.input,{minHeight:80,textAlignVertical:'top',marginBottom:16}]} multiline autoFocus
              placeholder="e.g. Updated section 4.2, corrected site boundary figures…"
              placeholderTextColor={C.light} value={verNote} onChangeText={setVerNote} />
            <TouchableOpacity style={s.submitBtn} onPress={handleAddVersion}>
              <Text style={s.submitBtnTxt}>Add Version</Text>
            </TouchableOpacity>
          </>
        ),
      })}

      {showReject && renderSmallSheet({
        visible:showReject, onClose:()=>setShowReject(false),
        icon:'close-circle-outline', iconColor:C.danger, title:'Reject Document',
        children: (
          <>
            <Text style={s.fLabel}>Rejection Reason *</Text>
            <TextInput style={[s.input,{minHeight:80,textAlignVertical:'top',marginBottom:16}]} multiline autoFocus
              placeholder="Explain why this document cannot be approved in its current form…"
              placeholderTextColor={C.light} value={rejectRsn} onChangeText={setRejectRsn} />
            <TouchableOpacity style={[s.submitBtn,{backgroundColor:C.danger}]} onPress={handleReject}>
              <Text style={s.submitBtnTxt}>Confirm Rejection</Text>
            </TouchableOpacity>
          </>
        ),
      })}

      {showComment && renderSmallSheet({
        visible:showComment, onClose:()=>setShowComment(false),
        icon:'comment-plus-outline', iconColor:C.accent, title:'Add Comment',
        children: (
          <>
            <TextInput style={[s.input,{minHeight:80,textAlignVertical:'top',marginBottom:16}]} multiline autoFocus
              placeholder="Your comment…" placeholderTextColor={C.light}
              value={newComment} onChangeText={setNewCmt} />
            <TouchableOpacity style={s.submitBtn} onPress={handleAddComment}>
              <Text style={s.submitBtnTxt}>Post Comment</Text>
            </TouchableOpacity>
          </>
        ),
      })}

      {showReminders && renderSmallSheet({
        visible:showReminders, onClose:()=>setShowReminders(false),
        icon:'bell-plus-outline', iconColor:C.warning, title:'Set Reminder',
        children: (
          <>
            <Text style={s.fLabel}>Reminder Date</Text>
            <TextInput style={[s.input,{marginBottom:12}]} placeholder="YYYY-MM-DD" placeholderTextColor={C.light}
              value={newRem.date} onChangeText={v=>setNewRem(p=>({...p,date:v}))} />
            <Text style={s.fLabel}>Note</Text>
            <TextInput style={[s.input,{minHeight:60,textAlignVertical:'top',marginBottom:16}]} multiline
              placeholder="e.g. Chase approval before renewal deadline" placeholderTextColor={C.light}
              value={newRem.note} onChangeText={v=>setNewRem(p=>({...p,note:v}))} />
            <TouchableOpacity style={s.submitBtn} onPress={handleAddReminder}>
              <Text style={s.submitBtnTxt}>Set Reminder</Text>
            </TouchableOpacity>
          </>
        ),
      })}
    </SafeAreaView>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  container:       { flex:1, backgroundColor:C.bg },
  loader:          { flex:1, justifyContent:'center', alignItems:'center', backgroundColor:C.bg },
  loaderTxt:       { marginTop:14, fontSize:FS_MD, color:C.mid },

  /* ── Header ── */
  header:          { paddingTop:12, paddingBottom:16, paddingHorizontal:HPAD },
  hRow:            { flexDirection:'row', alignItems:'center', marginBottom:13 },
  hBtn:            { width:ICON_BTN, height:ICON_BTN, borderRadius:ICON_BTN/2, backgroundColor:'rgba(255,255,255,0.2)', justifyContent:'center', alignItems:'center' },
  hLogo:           { width:36, height:36, borderRadius:8, backgroundColor:'rgba(255,255,255,0.15)', marginRight:4 },
  hLogoFallback:   { width:36, height:36, borderRadius:8, backgroundColor:'rgba(255,255,255,0.15)', justifyContent:'center', alignItems:'center', marginRight:4 },
  hTitle:          { fontSize:FS_XL, fontWeight:'700', color:'#fff', letterSpacing:-0.3 },
  hSub:            { fontSize:FS_SM, color:'rgba(255,255,255,0.82)', marginTop:2 },
  statsBar:        { flexDirection:'row', justifyContent:'space-around', marginBottom:13 },
  statItem:        { alignItems:'center', gap:3 },
  statVal:         { fontSize:FS_LG, fontWeight:'700', color:'#fff' },
  statLbl:         { fontSize:FS_XS, color:'rgba(255,255,255,0.78)' },
  searchBar:       { flexDirection:'row', alignItems:'center', backgroundColor:'rgba(255,255,255,0.96)', borderRadius:CARD_R, paddingHorizontal:14, paddingVertical:11, gap:10, marginTop:6 },
  searchInput:     { flex:1, fontSize:FS_MD, color:C.dark, letterSpacing:0.1 },

  /* ── Tabs ── */
  tab:             { flexDirection:'row', alignItems:'center', gap:5, paddingHorizontal:IS_PROMAX?12:10, paddingVertical:13, borderBottomWidth:2.5, borderBottomColor:'transparent' },
  tabActive:       { borderBottomColor:C.primary },
  tabTxt:          { fontSize:IS_PROMAX?13:12, fontWeight:'500', color:C.mid },
  tabTxtActive:    { color:C.primary, fontWeight:'700' },
  tabBadge:        { backgroundColor:'#e2e8f0', borderRadius:9, paddingHorizontal:6, paddingVertical:2 },
  tabBadgeActive:  { backgroundColor:`${C.primary}18` },
  tabBadgeTxt:     { fontSize:FS_XS, color:C.mid },

  /* ── Action bar ── */
  actionBar:       { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:HPAD, paddingVertical:10, backgroundColor:'#fff', borderBottomWidth:1, borderBottomColor:C.border },
  uploadBtn:       {},
  uploadBtnGrad:   { flexDirection:'row', alignItems:'center', gap:6, paddingHorizontal:16, paddingVertical:10, borderRadius:22 },
  uploadBtnTxt:    { fontSize:FS_SM, fontWeight:'700', color:'#fff' },
  iconBtn:         { width:ICON_BTN, height:ICON_BTN, borderRadius:ICON_BTN/2, justifyContent:'center', alignItems:'center', backgroundColor:'#f1f5f9' },

  /* ── Bulk bar ── */
  bulkBar:         { flexDirection:'row', alignItems:'center', backgroundColor:'#fff', paddingHorizontal:HPAD, paddingVertical:11, gap:16, borderBottomWidth:1, borderBottomColor:C.border },
  bulkCount:       { flex:1, fontSize:FS_BASE, fontWeight:'700', color:C.dark },
  bulkBtn:         { flexDirection:'row', alignItems:'center', gap:5 },
  bulkBtnTxt:      { fontSize:FS_SM, fontWeight:'600' },

  /* ── Result bar ── */
  resultBar:       { flexDirection:'row', alignItems:'center', flexWrap:'wrap', paddingHorizontal:HPAD, paddingVertical:9, gap:8 },
  resultCount:     { fontSize:FS_SM, fontWeight:'600', color:C.mid },
  activeFilterChip:{ flexDirection:'row', alignItems:'center', gap:5, backgroundColor:`${C.primary}12`, paddingHorizontal:10, paddingVertical:4, borderRadius:11 },
  activeFilterChipTxt:{ fontSize:FS_SM, fontWeight:'600', color:C.primary },

  /* ── List / Grid content ── */
  listContent:     { paddingHorizontal:HPAD, paddingTop:10, paddingBottom:110 },
  gridContent:     { paddingHorizontal:8, paddingTop:10, paddingBottom:110 },

  /* ── List card ── */
  card:            { backgroundColor:C.card, borderRadius:CARD_R, padding:IS_PROMAX?16:13, marginBottom:12, borderWidth:1, borderColor:C.border,
                     shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.05, shadowRadius:3, elevation:1 },
  cardSel:         { borderColor:C.primary, borderWidth:2 },
  cardExpiring:    { borderLeftWidth:3.5, borderLeftColor:C.warning },
  cardExpired:     { borderLeftWidth:3.5, borderLeftColor:C.danger },
  cardPinned:      { borderTopWidth:2.5, borderTopColor:C.gold },
  cardRow:         { flexDirection:'row', alignItems:'flex-start', marginBottom:10 },
  cardIcon:        { width:IS_PROMAX?46:42, height:IS_PROMAX?46:42, borderRadius:IS_PROMAX?23:21, justifyContent:'center', alignItems:'center' },
  cardNameRow:     { flexDirection:'row', alignItems:'center', marginBottom:4 },
  cardName:        { flex:1, fontSize:FS_MD, fontWeight:'700', color:C.dark, letterSpacing:-0.2 },
  cardBadges:      { flexDirection:'row', gap:4, marginLeft:7 },
  cardMeta:        { flexDirection:'row', alignItems:'center', gap:5, flexWrap:'wrap' },
  cardMetaTxt:     { fontSize:FS_SM, color:C.light },
  dot:             { fontSize:FS_SM, color:C.light },
  cardDesc:        { fontSize:FS_SM, color:C.mid, lineHeight:IS_PROMAX?20:17, marginBottom:10 },
  cardFooter:      { flexDirection:'row', justifyContent:'space-between', alignItems:'flex-start', marginTop:8 },
  cardStats:       { flexDirection:'row', gap:14 },
  cardStat:        { flexDirection:'row', alignItems:'center', gap:4 },
  cardStatTxt:     { fontSize:FS_SM, color:C.light },
  cardDate:        { fontSize:FS_XS, color:C.light },
  alertBar:        { flexDirection:'row', alignItems:'center', gap:7, backgroundColor:'#fffbeb', paddingHorizontal:12, paddingVertical:8, borderRadius:8, marginTop:10 },
  alertBarTxt:     { fontSize:FS_SM, fontWeight:'600' },

  /* ── Status / access pills ── */
  statusPill:      { paddingHorizontal:9, paddingVertical:4, borderRadius:11 },
  statusPillTxt:   { fontSize:FS_XS, fontWeight:'700' },
  accessPill:      { flexDirection:'row', alignItems:'center', gap:3, paddingHorizontal:8, paddingVertical:3, borderRadius:8 },
  accessPillTxt:   { fontSize:11, fontWeight:'600', flexShrink:0 },

  /* ── Tags ── */
  tagsRow:         { flexDirection:'row', flexWrap:'wrap', gap:5, marginBottom:8 },
  tagChip:         { backgroundColor:'#f1f5f9', paddingHorizontal:8, paddingVertical:3, borderRadius:8 },
  tagChipTxt:      { fontSize:FS_XS, fontWeight:'500', color:C.mid },

  /* ── Grid card ── */
  gridCard:        { flex:1, margin:IS_PROMAX?6:5, backgroundColor:C.card, borderRadius:CARD_R, padding:IS_PROMAX?14:12, alignItems:'center', borderWidth:1, borderColor:C.border, minHeight:IS_PROMAX?145:130,
                     shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.04, shadowRadius:2, elevation:1 },
  gridIconWrap:    { width:IS_PROMAX?60:54, height:IS_PROMAX?60:54, borderRadius:IS_PROMAX?30:27, justifyContent:'center', alignItems:'center', marginBottom:10 },
  gridName:        { fontSize:IS_PROMAX?13:12, fontWeight:'700', color:C.dark, textAlign:'center', marginBottom:4 },
  gridMeta:        { fontSize:FS_XS, color:C.light, textAlign:'center' },
  gridStatusDot:   { width:9, height:9, borderRadius:4.5, marginTop:7 },
  gridPendBadge:   { position:'absolute', top:8, right:8, backgroundColor:'#fffbeb', paddingHorizontal:6, paddingVertical:2, borderRadius:7 },
  gridPendText:    { fontSize:10, color:C.warning, fontWeight:'700' },
  gridExpBadge:    { position:'absolute', top:8, left:8, paddingHorizontal:6, paddingVertical:2, borderRadius:7 },
  gridExpText:     { fontSize:10, color:'#fff', fontWeight:'700' },
  gridPin:         { position:'absolute', top:7, right:7 },

  /* ── Bulk check dot ── */
  bulkDot:         { width:22, height:22, borderRadius:11, borderWidth:2, borderColor:C.border, backgroundColor:C.card, justifyContent:'center', alignItems:'center', position:'absolute', top:9, left:9, zIndex:1 },
  bulkDotOn:       { backgroundColor:C.primary, borderColor:C.primary },

  /* ── Empty state ── */
  empty:           { alignItems:'center', paddingVertical:IS_PROMAX?80:70, paddingHorizontal:40 },
  emptyTitle:      { fontSize:FS_XL, fontWeight:'700', color:C.mid, marginTop:16 },
  emptySub:        { fontSize:FS_BASE, color:C.light, marginTop:8, textAlign:'center', lineHeight:22 },
  emptyAction:     { marginTop:18, paddingHorizontal:22, paddingVertical:12, backgroundColor:`${C.primary}18`, borderRadius:22 },
  emptyActionTxt:  { fontSize:FS_BASE, fontWeight:'700', color:C.primary },
  emptyMini:       { fontSize:FS_SM, color:C.light, fontStyle:'italic', paddingVertical:5 },

  /* ── FAB ── */
  fab:             { position:'absolute', right:IS_PROMAX?22:18, bottom:FAB_BOTTOM, elevation:10, shadowColor:C.primary, shadowOffset:{width:0,height:5}, shadowOpacity:0.35, shadowRadius:10 },
  fabGrad:         { width:FAB_SIZE, height:FAB_SIZE, borderRadius:FAB_SIZE/2, justifyContent:'center', alignItems:'center' },

  /* ── Modal full screen ── */
  modalFull:       { flex:1, backgroundColor:C.bg },
  modalHd:         { flexDirection:'row', alignItems:'center', paddingHorizontal:HPAD, paddingTop:40, paddingBottom:16 },
  modalTitle:      { flex:1, fontSize:FS_LG, fontWeight:'700', color:'#fff', textAlign:'center', marginHorizontal:8 },
  modalSave:       { fontSize:FS_MD, fontWeight:'700', color:'#fff' },

  /* ── Bottom sheet ── */
  sheet:           { flex:1, backgroundColor:'rgba(0,0,0,0.5)', justifyContent:'flex-end' },
  sheetBox:        { backgroundColor:'#fff', borderTopLeftRadius:MODAL_R, borderTopRightRadius:MODAL_R },
  sheetHd:         {},
  sheetHandle:     { width:44, height:5, backgroundColor:C.border, borderRadius:3, alignSelf:'center', marginTop:12, marginBottom:10 },
  sheetTitle:      { fontSize:FS_LG, fontWeight:'700', color:C.dark },
  sheetActions:    { flexDirection:'row', gap:12, padding:HPAD, borderTopWidth:1, borderTopColor:C.border },

  /* ── Form ── */
  formBody:        { padding:HPAD },
  fLabel:          { fontSize:FS_XS, fontWeight:'700', color:C.mid, marginBottom:8, marginTop:2, textTransform:'uppercase', letterSpacing:0.6 },
  fRow:            { flexDirection:'row' },
  fActions:        { flexDirection:'row', gap:12, marginTop:12 },
  input:           { backgroundColor:'#f8fafc', borderRadius:CARD_R, borderWidth:1, borderColor:C.border, paddingHorizontal:14, paddingVertical:IS_PROMAX?13:11, fontSize:FS_MD, color:C.dark, marginBottom:16, letterSpacing:0.1 },
  chipRow:         { flexDirection:'row', flexWrap:'wrap', gap:8, marginBottom:16 },
  chip:            { flexDirection:'row', alignItems:'center', gap:5, paddingHorizontal:13, paddingVertical:8, borderRadius:22, backgroundColor:'#f1f5f9', borderWidth:1, borderColor:C.border },
  chipTxt:         { fontSize:FS_SM, fontWeight:'500', color:C.mid },
  filePicker:      { alignItems:'center', borderWidth:2, borderColor:C.border, borderStyle:'dashed', borderRadius:CARD_R, paddingVertical:IS_PROMAX?30:24, marginBottom:20, gap:8 },
  filePickerTxt:   { fontSize:FS_MD, fontWeight:'700', color:C.primary },
  filePickerSub:   { fontSize:FS_SM, color:C.light },
  cancelBtn:       { flex:1, paddingVertical:IS_PROMAX?14:12, borderRadius:CARD_R, alignItems:'center', backgroundColor:'#f1f5f9', borderWidth:1, borderColor:C.border },
  cancelBtnTxt:    { fontSize:FS_MD, fontWeight:'600', color:C.mid },
  submitBtn:       { flex:2, paddingVertical:IS_PROMAX?14:12, borderRadius:CARD_R, alignItems:'center', backgroundColor:C.primary },
  submitBtnTxt:    { fontSize:FS_MD, fontWeight:'700', color:'#fff', letterSpacing:0.2 },
  filterSecTitle:  { fontSize:FS_XS, fontWeight:'700', color:C.dark, marginBottom:9, marginTop:12, textTransform:'uppercase', letterSpacing:0.6 },

  /* ── Sort sheet ── */
  sortRow:         { flexDirection:'row', alignItems:'center', justifyContent:'space-between', paddingHorizontal:HPAD, paddingVertical:IS_PROMAX?15:13, borderRadius:10 },
  sortRowActive:   { backgroundColor:`${C.primary}10` },
  sortRowTxt:      { fontSize:FS_MD, color:C.mid },

  /* ── Detail hero ── */
  detHero:         { alignItems:'center', paddingVertical:IS_PROMAX?24:18, marginBottom:8 },
  detHeroIcon:     { width:AVATAR_LG, height:AVATAR_LG, borderRadius:AVATAR_LG/2, justifyContent:'center', alignItems:'center', marginBottom:12 },
  detHeroName:     { fontSize:FS_XL, fontWeight:'800', color:C.dark, textAlign:'center', marginBottom:4, letterSpacing:-0.4 },
  detHeroRef:      { fontSize:FS_SM, fontWeight:'600', color:C.light, marginBottom:3 },
  detHeroCat:      { fontSize:FS_SM, color:C.light, fontWeight:'500' },
  expiryBanner:    { flexDirection:'row', alignItems:'center', gap:7, paddingHorizontal:14, paddingVertical:9, borderRadius:CARD_R-2, marginTop:12 },
  expiryBannerTxt: { fontSize:FS_SM, fontWeight:'700' },

  /* ── Quick action row ── */
  quickRow:        { flexDirection:'row', flexWrap:'wrap', justifyContent:'space-around', backgroundColor:C.card, padding:IS_PROMAX?16:12, borderRadius:CARD_R, marginBottom:12, borderWidth:1, borderColor:C.border, gap:6 },
  quickBtn:        { alignItems:'center', gap:6, minWidth:IS_PROMAX?64:60 },
  quickBtnIcon:    { width:AVATAR_SM, height:AVATAR_SM, borderRadius:AVATAR_SM/2, justifyContent:'center', alignItems:'center' },
  quickBtnLbl:     { fontSize:FS_XS, fontWeight:'600', color:C.mid, textAlign:'center' },

  /* ── Section block ── */
  sec:             { backgroundColor:C.card, borderRadius:CARD_R, padding:IS_PROMAX?16:13, marginBottom:12, borderWidth:1, borderColor:C.border,
                     shadowColor:'#000', shadowOffset:{width:0,height:1}, shadowOpacity:0.04, shadowRadius:2, elevation:1 },
  secTitle:        { fontSize:FS_XS, fontWeight:'700', color:C.mid, marginBottom:12, textTransform:'uppercase', letterSpacing:0.7 },
  secBody:         { fontSize:FS_BASE, color:C.mid, lineHeight:IS_PROMAX?22:19 },
  secHdRow:        { flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:12 },
  dRow:            { flexDirection:'row', justifyContent:'space-between', alignItems:'center', paddingVertical:9, borderBottomWidth:1, borderBottomColor:'#f8fafc' },
  dLabel:          { fontSize:FS_BASE, color:C.mid },
  dVal:            { fontSize:FS_BASE, fontWeight:'600', color:C.dark, maxWidth:'60%', textAlign:'right', flexShrink:1 },

  /* ── Approval buttons ── */
  approveBtn:      { flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:7, backgroundColor:C.success, paddingVertical:IS_PROMAX?13:11, borderRadius:CARD_R },
  approveBtnTxt:   { fontSize:FS_MD, fontWeight:'700', color:'#fff' },
  rejectBtn:       { flex:1, flexDirection:'row', alignItems:'center', justifyContent:'center', gap:7, backgroundColor:C.danger, paddingVertical:IS_PROMAX?13:11, borderRadius:CARD_R },
  rejectBtnTxt:    { fontSize:FS_MD, fontWeight:'700', color:'#fff' },

  /* ── Danger zone ── */
  dangerBtn:       { flexDirection:'row', alignItems:'center', gap:7, backgroundColor:'#f8fafc', borderWidth:1, borderColor:C.border, paddingHorizontal:16, paddingVertical:IS_PROMAX?12:10, borderRadius:CARD_R },
  dangerBtnTxt:    { fontSize:FS_BASE, fontWeight:'700' },

  /* ── Versions ── */
  verRow:          { flexDirection:'row', alignItems:'flex-start', paddingVertical:30 },
  verItemFull:     { flexDirection:'row', alignItems:'flex-start', paddingVertical:12 },
  verBadge:        { paddingHorizontal:10, paddingVertical:5, backgroundColor:`${C.primary}18`, borderRadius:11, alignSelf:'flex-start' },
  verBadgeTxt:     { fontSize:FS_SM, fontWeight:'700', color:C.primary },
  verChanges:      { fontSize:FS_BASE, color:C.dark, fontWeight:'500' },
  verMeta:         { fontSize:FS_SM, color:C.light, marginTop:3 },
  latestBadge:     { backgroundColor:`${C.success}18`, paddingHorizontal:8, paddingVertical:4, borderRadius:9 },
  latestBadgeTxt:  { fontSize:FS_XS, fontWeight:'700', color:C.success },
  miniBtn:         { flexDirection:'row', alignItems:'center', gap:5, paddingHorizontal:11, paddingVertical:6, borderRadius:11, backgroundColor:`${C.primary}14` },
  miniBtnTxt:      { fontSize:FS_SM, fontWeight:'700', color:C.primary },
  viewAll:         { fontSize:FS_BASE, fontWeight:'700', color:C.primary },
  addBtn:          { width:32, height:32, borderRadius:16, backgroundColor:`${C.primary}14`, justifyContent:'center', alignItems:'center' },

  /* ── Comments ── */
  cmtItem:         { flexDirection:'row', alignItems:'flex-start', paddingVertical:11, borderBottomWidth:1, borderBottomColor:'#f8fafc' },
  cmtAvatar:       { width:36, height:36, borderRadius:18, backgroundColor:C.primary, justifyContent:'center', alignItems:'center' },
  cmtAvatarTxt:    { fontSize:FS_BASE, fontWeight:'700', color:'#fff' },
  cmtAuthor:       { fontSize:FS_SM, fontWeight:'700', color:C.dark },
  cmtDate:         { fontSize:FS_XS, color:C.light },
  cmtText:         { fontSize:FS_BASE, color:C.mid, marginTop:3, lineHeight:IS_PROMAX?21:18 },
  cmtResolve:      { fontSize:FS_SM, color:C.light, marginTop:5, fontWeight:'600' },

  /* ── Reminders ── */
  remRow:          { flexDirection:'row', alignItems:'flex-start', paddingVertical:10, borderBottomWidth:1, borderBottomColor:'#f8fafc' },
  remDate:         { fontSize:FS_SM, fontWeight:'700', color:C.dark },
  remNote:         { fontSize:FS_SM, color:C.mid, marginTop:2 },

  /* ── Share trail ── */
  stRow:           { flexDirection:'row', alignItems:'center', gap:7, paddingVertical:6 },
  stTxt:           { fontSize:FS_SM, color:C.mid },
  stRowFull:       { flexDirection:'row', alignItems:'center', paddingVertical:12, borderBottomWidth:1, borderBottomColor:'#f8fafc' },

  /* ── Stats card ── */
  statCard:        { flex:1, backgroundColor:'#f8fafc', borderRadius:CARD_R-2, padding:IS_PROMAX?13:11, alignItems:'center', gap:4 },
  statCardVal:     { fontSize:IS_PROMAX?22:20, fontWeight:'800', color:C.dark },
  statCardLbl:     { fontSize:FS_SM, color:C.light },

  /* ── Analytics KPI ── */
  kpiGrid:         { flexDirection:'row', flexWrap:'wrap', gap:IS_PROMAX?10:8, marginBottom:10 },
  kpiCard:         { width:(W - HPAD*2 - 30) / 4, backgroundColor:C.card, borderRadius:CARD_R, padding:IS_PROMAX?12:10, alignItems:'center', gap:4, borderWidth:1, borderColor:C.border },
  kpiVal:          { fontSize:IS_PROMAX?22:20, fontWeight:'800' },
  kpiLbl:          { fontSize:10, color:C.light, textAlign:'center', lineHeight:13 },

  /* ── Analytics bars ── */
  barRow:          { flexDirection:'row', alignItems:'center', gap:10, marginBottom:9 },
  barLbl:          { flexDirection:'row', alignItems:'center', gap:5, width:IS_PROMAX?96:88 },
  barLblTxt:       { fontSize:FS_SM, color:C.mid, flex:1 },
  barTrack:        { flex:1, height:IS_PROMAX?10:8, backgroundColor:'#f1f5f9', borderRadius:5, overflow:'hidden' },
  barFill:         { height:'100%', borderRadius:5 },
  barCount:        { fontSize:FS_SM, fontWeight:'700', color:C.dark, width:26, textAlign:'right' },

  /* ── Analytics rank ── */
  rankRow:         { flexDirection:'row', alignItems:'center', paddingVertical:42, borderBottomWidth:1, borderBottomColor:'#f1f5f9' },
  rankNum:         { fontSize:IS_PROMAX?18:16, fontWeight:'800', color:C.primary, width:32 },
  rankName:        { fontSize:FS_BASE, fontWeight:'600', color:C.dark },
  rankMeta:        { fontSize:FS_SM, color:C.light, marginTop:2 },
});
